import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useRealtimeRefresh } from '../../hooks/useRealtimeRefresh';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { PackageMinus, Clock, User, Package, Search, Building2, FileText, Download, Eye, Filter, X, Calendar, Trash2, Lock, Plus, ChevronsUpDown, Check, ArrowUpDown, ArrowUp, ArrowDown, UserCheck, History, AlertTriangle, Wrench, HelpCircle, Upload, Paperclip, Info, ClipboardList, CheckCircle } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Textarea } from '../../components/ui/textarea';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Demirbaş geçmiş kaydını anlaşılır bir olaya dönüştür
const USAGE_LABELS = {
  depoda: 'Depoda',
  zimmette: 'Kişide (Kalıcı Zimmet)',
  kullanicida: 'Kişide (Geçici)',
  bozuk: 'Bozuk',
  tamirde: 'Tamirde',
  kayip: 'Kayıp',
  demirbas: 'Demirbaş',
};

const parseChangeList = (changes) => {
  const map = {};
  (changes || []).forEach((c) => {
    const idx = String(c).indexOf(': ');
    if (idx === -1) return;
    const field = c.slice(0, idx).trim();
    const rest = c.slice(idx + 2);
    const parts = rest.split(' → ');
    map[field] = { old: (parts[0] || '').trim(), new: (parts[1] || '').trim() };
  });
  return map;
};

const extractRecipient = (text) => {
  if (!text) return null;
  const m = String(text).match(/(?:Zimmette|Kullan[ıi]c[ıi]da|Kullan[ıi]c[ıi])\s*:\s*(.+)/i);
  return m ? m[1].trim() : null;
};

// Returns { iconKey, title, color, rows:[{label,value}], note }
const interpretDemirbasEvent = (h) => {
  const map = parseChangeList(h.changes);
  const usage = map['Kullanım Durumu'];
  const konum = map['Konum'];
  const sonDurum = map['Son Durum'];
  const desc = (h.description && h.description !== 'İlk kayıt') ? h.description : '';
  const performer = h.user_name || '-';
  const rows = [];

  // Depoda teslim alma
  if (desc && /teslim al[ıi]nd/i.test(desc)) {
    rows.push({ label: 'Teslim Alan (Depo)', value: performer });
    if (h.location) rows.push({ label: 'Şube', value: h.location });
    return { iconKey: 'received', title: 'Depoda Teslim Alındı', color: 'emerald', rows, note: '' };
  }

  // Kişiye zimmetleme / verme
  if (usage && (usage.new === 'zimmette' || usage.new === 'kullanicida')) {
    const recipient = extractRecipient(sonDurum && sonDurum.new) || extractRecipient(h.last_status);
    rows.push({ label: 'Teslim Eden (Depo)', value: performer });
    if (recipient) rows.push({ label: 'Teslim Alan', value: recipient });
    rows.push({ label: 'Zimmet Türü', value: usage.new === 'zimmette' ? 'Kalıcı Zimmet' : 'Geçici (Kullanıcıda)' });
    return {
      iconKey: 'assign',
      title: usage.new === 'zimmette' ? 'Kişiye Zimmetlendi (Kalıcı)' : 'Kişiye Verildi (Geçici)',
      color: 'indigo', rows, note: (desc && !/zimmette|kullan/i.test(desc)) ? desc : ''
    };
  }

  // Depoya geri alma
  if (usage && usage.new === 'depoda' && (usage.old === 'zimmette' || usage.old === 'kullanicida')) {
    rows.push({ label: 'Geri Alan (Depo)', value: performer });
    const prevRecipient = extractRecipient(sonDurum && sonDurum.old);
    if (prevRecipient) rows.push({ label: 'Önceki Kullanıcı', value: prevRecipient });
    return { iconKey: 'return', title: 'Depoya Geri Alındı', color: 'orange', rows, note: '' };
  }

  // Bozuk / tamirde / kayıp
  if (usage && ['bozuk', 'tamirde', 'kayip'].includes(usage.new)) {
    rows.push({ label: 'İşlemi Yapan', value: performer });
    rows.push({ label: 'Yeni Durum', value: USAGE_LABELS[usage.new] });
    return { iconKey: 'status', title: `Durum: ${USAGE_LABELS[usage.new]}`, color: 'red', rows, note: desc };
  }

  // Şube transferi (konum değişikliği)
  if (konum && konum.new) {
    rows.push({ label: 'İşlemi Yapan', value: performer });
    rows.push({ label: 'Nereden', value: konum.old || 'Belirtilmemiş' });
    rows.push({ label: 'Nereye', value: konum.new });
    return { iconKey: 'transfer', title: 'Şube Transferi', color: 'blue', rows, note: '' };
  }

  // İlk kayıt
  if (h.id === 'creation' || (h.changes || []).some((c) => /oluşturuldu/i.test(c))) {
    rows.push({ label: 'Oluşturan', value: performer });
    if (h.location) rows.push({ label: 'Konum', value: h.location });
    return { iconKey: 'create', title: 'Demirbaş Kaydı Oluşturuldu', color: 'slate', rows, note: '' };
  }

  // Durum notu güncellemesi (yalnızca Son Durum değişti)
  rows.push({ label: 'İşlemi Yapan', value: performer });
  if (sonDurum) {
    rows.push({ label: 'Önceki Not', value: sonDurum.old || '-' });
    rows.push({ label: 'Yeni Not', value: sonDurum.new || '-' });
  }
  return { iconKey: 'note', title: 'Durum Notu Güncellendi', color: 'gray', rows, note: desc };
};


const DepotExit = ({ branchIdOverride, readOnly = false }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('stok-cikis');
  const [stokCikisleri, setStokCikisleri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Bilgilendirme kutusu state'leri (localStorage'dan oku)
  const [showStokCikisInfo, setShowStokCikisInfo] = useState(() => {
    return localStorage.getItem('depot_stok_cikis_info_hidden') !== 'true';
  });
  const [showZimmetInfo, setShowZimmetInfo] = useState(() => {
    return localStorage.getItem('depot_zimmet_info_hidden') !== 'true';
  });
  
  // Zimmet State
  const [zimmetler, setZimmetler] = useState([]);
  const [zimmetLoading, setZimmetLoading] = useState(false);
  const [showZimmetModal, setShowZimmetModal] = useState(false);
  const [showZimmetVerModal, setShowZimmetVerModal] = useState(false);
  const [showGecmisModal, setShowGecmisModal] = useState(false);
  const [selectedZimmet, setSelectedZimmet] = useState(null);
  const [zimmetGecmisi, setZimmetGecmisi] = useState([]);
  const [zimmetForm, setZimmetForm] = useState({
    urun_adi: '',
    marka: '',
    model: '',
    seri_no: '',
    aciklama: ''
  });
  const [zimmetVerForm, setZimmetVerForm] = useState({
    kullanici_adi: '',
    kullanici_id: '',
    aciklama: ''
  });
  const [zimmetSearchTerm, setZimmetSearchTerm] = useState('');
  const [zimmetSortConfig, setZimmetSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  // Tutanak Oluştur (Teslim Etme / Teslim Alma PDF)
  const [tutanakModalOpen, setTutanakModalOpen] = useState(false);
  const [tutanakKisiler, setTutanakKisiler] = useState([]);
  const [tutanakSubmitting, setTutanakSubmitting] = useState(false);
  const [edenPickerOpen, setEdenPickerOpen] = useState(false);
  const [alanPickerOpen, setAlanPickerOpen] = useState(false);
  const [tutanakForm, setTutanakForm] = useState({
    tutanak_turu: 'teslim_etme',
    teslim_eden: '',
    teslim_alan: '',
    urun_adi: '',
    marka: '',
    model: '',
    seri_no: '',
    aciklama: '',
    iade_tarihi: '',
  });
  
  // Şube çalışanları listesi
  const [subeCalisanlari, setSubeCalisanlari] = useState([]);
  
  // Taşeronlar State
  const [taseronlar, setTaseronlar] = useState([]);
  const [taseronLoading, setTaseronLoading] = useState(false);
  const [selectedTaseron, setSelectedTaseron] = useState(null);
  const [taseronMalzemeleri, setTaseronMalzemeleri] = useState([]);
  const [taseronMalzemeLoading, setTaseronMalzemeLoading] = useState(false);
  const [showTaseronMalzemeModal, setShowTaseronMalzemeModal] = useState(false);
  const [taseronMalzemeForm, setTaseronMalzemeForm] = useState({
    stok_karti_id: '',
    urun_adi: '',
    miktar: '',
    birim: 'adet',
    teslim_alan: '',
    aciklama: '',
    belgeler: []
  });
  const [taseronSearchTerm, setTaseronSearchTerm] = useState('');
  const [showTaseronInfo, setShowTaseronInfo] = useState(() => {
    return localStorage.getItem('depot_taseron_info_hidden') !== 'true';
  });
  
  // Teslim Alanlar State
  const [teslimAlanlar, setTeslimAlanlar] = useState([]);
  const [teslimAlanlarLoading, setTeslimAlanlarLoading] = useState(false);
  const [selectedTeslimAlan, setSelectedTeslimAlan] = useState(null);
  const [teslimAlanSearchTerm, setTeslimAlanSearchTerm] = useState('');
  const [showTeslimAlanlarInfo, setShowTeslimAlanlarInfo] = useState(() => {
    return localStorage.getItem('depot_teslim_alanlar_info_hidden') !== 'true';
  });
  
  
  // Demirbaşlar State - KALDIRILDI, artık zimmetler içinde gösterilecek
  const [showDemirbasInfo, setShowDemirbasInfo] = useState(() => {
    return localStorage.getItem('depot_demirbas_info_hidden') !== 'true';
  });
  const [showDemirbasDetailModal, setShowDemirbasDetailModal] = useState(false);
  const [selectedDemirbas, setSelectedDemirbas] = useState(null);
  
  // Durum Değişikliği Modal State
  const [showDurumModal, setShowDurumModal] = useState(false);
  const [durumChangeData, setDurumChangeData] = useState({
    zimmet: null,
    yeniDurum: '',
    aciklama: '',
    kullanici_adi: '',
    // Durum-specific fields
    tamir_yeri: '',
    tahmini_sure: '',
    bozukluk_nedeni: '',
    kayip_tarihi: '',
    kayip_yeri: '',
    // Belge alanı
    belge: []
  });
  const [durumBelgeUploading, setDurumBelgeUploading] = useState(false);
  
  // Stok Çıkış Ekle Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [stokKartlari, setStokKartlari] = useState([]);
  const [stokDurumu, setStokDurumu] = useState([]);
  const [addForm, setAddForm] = useState({
    requester_name: '',
    teslim_alan: '',
    teslim_alan_id: '',
    taseron_firma: '',
    description: '',
    items: [{ stok_karti_id: '', quantity: '', unit: 'adet' }]
  });
  const [submitting, setSubmitting] = useState(false);
  const [stokKartiOpen, setStokKartiOpen] = useState({});

  // Filter states
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    requester: '',
    product: '',
    taseronFirma: '',
    createdBy: ''
  });

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Sort state
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  // Upload state
  const [uploadingCikisId, setUploadingCikisId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(false);

  // Şifre Doğrulama State
  const [passwordModal, setPasswordModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  const EDIT_DELETE_PASSWORD = 'Alyatugba21';

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('fortis_token')}`,
    'Content-Type': 'application/json'
  });

  // Şifre ile işlem başlatma
  const requestPasswordForAction = (action, data) => {
    setPendingAction({ action, data });
    setPasswordInput('');
    setPasswordModal(true);
  };

  // Şifre doğrulama ve işlem yürütme
  const handlePasswordConfirm = () => {
    if (passwordInput !== EDIT_DELETE_PASSWORD) {
      toast.error('Şifre yanlış!');
      return;
    }
    
    setPasswordModal(false);
    setPasswordInput('');
    
    if (pendingAction) {
      const { action, data } = pendingAction;
      switch (action) {
        case 'deleteStokCikisi':
          executeDeleteStokCikisi(data);
          break;
        default:
          break;
      }
      setPendingAction(null);
    }
  };

  const fetchStokCikisleri = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const branchParam = branchIdOverride ? `?branch_id=${branchIdOverride}` : '';
      const res = await axios.get(`${API}/api/depot/stok-cikisleri${branchParam}`, { headers: getAuthHeaders() });
      setStokCikisleri(res.data);
    } catch (err) {
      console.error('Stok çıkışları yüklenemedi:', err);
      setError('Stok çıkışları yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [branchIdOverride]);

  const handleTamamlaReddedilen = async (cikisId) => {
    try {
      await axios.post(`${API}/api/depot/stok-cikisleri/${cikisId}/tamamla`, {}, { headers: getAuthHeaders() });
      toast.success('Reddedilen çıkış tamamlandı ve arşivlendi');
      fetchStokCikisleri();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleTekrarAktifEt = async (cikisId) => {
    try {
      await axios.post(`${API}/api/depot/stok-cikisleri/${cikisId}/tekrar-aktif`, {}, { headers: getAuthHeaders() });
      toast.success('Çıkış tekrar aktif edildi');
      fetchStokCikisleri();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    }
  };


  useEffect(() => {
    fetchStokCikisleri();
  }, [fetchStokCikisleri]);

  // Zimmet fonksiyonları
  const fetchZimmetler = useCallback(async () => {
    try {
      setZimmetLoading(true);
      const branchParam = branchIdOverride ? `?branch_id=${branchIdOverride}` : '';
      const res = await axios.get(`${API}/api/depot/zimmetler${branchParam}`, { headers: getAuthHeaders() });
      setZimmetler(res.data);
    } catch (err) {
      console.error('Zimmetler yüklenemedi:', err);
      toast.error('Zimmetler yüklenemedi');
    } finally {
      setZimmetLoading(false);
    }
  }, [branchIdOverride]);

  // Şube çalışanlarını getir
  const fetchSubeCalisanlari = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/users/branch-employees`, { headers: getAuthHeaders() });
      setSubeCalisanlari(res.data);
    } catch (err) {
      console.error('Şube çalışanları yüklenemedi:', err);
    }
  }, []);

  // Taşeronlar fonksiyonları
  const fetchTaseronlar = useCallback(async () => {
    try {
      setTaseronLoading(true);
      const res = await axios.get(`${API}/api/depot/taseronlar`, { headers: getAuthHeaders() });
      setTaseronlar(res.data);
    } catch (err) {
      console.error('Taşeronlar yüklenemedi:', err);
      toast.error('Taşeronlar yüklenemedi');
    } finally {
      setTaseronLoading(false);
    }
  }, []);

  const fetchTeslimAlanlar = useCallback(async () => {
    try {
      setTeslimAlanlarLoading(true);
      const res = await axios.get(`${API}/api/depot/teslim-alanlar`, { headers: getAuthHeaders() });
      setTeslimAlanlar(res.data);
    } catch (err) {
      console.error('Teslim alanlar yüklenemedi:', err);
      toast.error('Teslim alanlar yüklenemedi');
    } finally {
      setTeslimAlanlarLoading(false);
    }
  }, []);

  // fetchDemirbaslar fonksiyonu KALDIRILDI - artık zimmetler içinde gelecek

  const handleTeslimAlanSelect = (kisi) => {
    setSelectedTeslimAlan(selectedTeslimAlan?.ad === kisi.ad ? null : kisi);
  };

  const fetchTaseronMalzemeleri = useCallback(async (taseronId) => {
    try {
      setTaseronMalzemeLoading(true);
      const res = await axios.get(`${API}/api/depot/taseronlar/${taseronId}/malzemeler`, { headers: getAuthHeaders() });
      setTaseronMalzemeleri(res.data);
    } catch (err) {
      console.error('Taşeron malzemeleri yüklenemedi:', err);
      toast.error('Malzemeler yüklenemedi');
    } finally {
      setTaseronMalzemeLoading(false);
    }
  }, []);

  const handleTaseronSelect = (taseron) => {
    setSelectedTaseron(taseron);
    fetchTaseronMalzemeleri(taseron.id);
  };

  const handleTaseronMalzemeSubmit = async () => {
    if (!selectedTaseron) {
      toast.error('Taşeron seçilmedi');
      return;
    }
    if (!taseronMalzemeForm.urun_adi && !taseronMalzemeForm.stok_karti_id) {
      toast.error('Ürün adı veya stok kartı seçilmelidir');
      return;
    }
    if (!taseronMalzemeForm.miktar || parseFloat(taseronMalzemeForm.miktar) <= 0) {
      toast.error('Geçerli bir miktar giriniz');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(
        `${API}/api/depot/taseronlar/${selectedTaseron.id}/malzemeler`,
        taseronMalzemeForm,
        { headers: getAuthHeaders() }
      );
      toast.success('Malzeme taşerona verildi');
      setShowTaseronMalzemeModal(false);
      setTaseronMalzemeForm({
        stok_karti_id: '',
        urun_adi: '',
        miktar: '',
        birim: 'adet',
        teslim_alan: '',
        aciklama: '',
        belgeler: []
      });
      fetchTaseronMalzemeleri(selectedTaseron.id);
      fetchTaseronlar(); // Malzeme sayısını güncelle
      fetchStokKartlari(); // Stok durumunu güncelle
    } catch (err) {
      console.error('Malzeme verilemedi:', err);
      toast.error(err.response?.data?.detail || 'Malzeme verilemedi');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTaseronMalzeme = async (malzemeId) => {
    if (!window.confirm('Bu malzeme kaydını silmek istediğinize emin misiniz? Stok geri eklenecektir.')) {
      return;
    }
    try {
      await axios.delete(`${API}/api/depot/taseronlar/malzemeler/${malzemeId}`, { headers: getAuthHeaders() });
      toast.success('Malzeme kaydı silindi');
      if (selectedTaseron) {
        fetchTaseronMalzemeleri(selectedTaseron.id);
      }
      fetchTaseronlar();
      fetchStokKartlari();
    } catch (err) {
      console.error('Malzeme silinemedi:', err);
      toast.error('Malzeme silinemedi');
    }
  };

  useEffect(() => {
    // Teslim alan / zimmet seçimleri için şube kullanıcıları her sekmede gerekli
    fetchSubeCalisanlari();
    if (activeTab === 'zimmetler') {
      fetchZimmetler();
      fetchSubeCalisanlari();
    } else if (activeTab === 'taseronlar') {
      fetchTaseronlar();
      fetchStokKartlari();
    } else if (activeTab === 'teslim-alanlar') {
      fetchTeslimAlanlar();
    }
    // 'demirbaslar' tab kaldırıldı - artık zimmetler içinde
  }, [activeTab, fetchZimmetler, fetchSubeCalisanlari, fetchTaseronlar, fetchTeslimAlanlar]);

  // Gerçek zamanlı: veri değişince listeleri otomatik yenile
  useRealtimeRefresh(() => {
    fetchStokCikisleri();
    if (activeTab === 'zimmetler') fetchZimmetler();
  });

  // Zimmet sıralama fonksiyonu
  const handleZimmetSort = (key) => {
    setZimmetSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getZimmetSortIcon = (key) => {
    if (zimmetSortConfig.key !== key) return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    return zimmetSortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1" /> 
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  const handleAddZimmet = async () => {
    if (!zimmetForm.urun_adi) {
      toast.error('Ürün adı gerekli');
      return;
    }
    try {
      await axios.post(`${API}/api/depot/zimmetler`, zimmetForm, { headers: getAuthHeaders() });
      toast.success('Zimmet ürünü eklendi');
      setShowZimmetModal(false);
      setZimmetForm({ urun_adi: '', marka: '', model: '', seri_no: '', aciklama: '' });
      fetchZimmetler();
    } catch (err) {
      toast.error('Zimmet eklenemedi');
    }
  };

  // Demirbaş teslim al (Zimmetler - Demirbaşlar)
  const handleDemirbasTeslimAl = async (zimmet) => {
    const demirbasId = zimmet.demirbas_id || zimmet.demirbas_data?.id || zimmet.id;
    if (!window.confirm(`"${zimmet.urun_adi}" ürününü teslim aldığınızı onaylıyor musunuz?`)) return;
    try {
      await axios.post(`${API}/api/purchasing/demirbaslar/${demirbasId}/depot-teslim-al`, {}, { headers: getAuthHeaders() });
      toast.success('Demirbaş teslim alındı olarak işaretlendi');
      fetchZimmetler();
    } catch (error) {
      console.error('Demirbaş teslim alma hatası:', error);
      toast.error(error.response?.data?.detail || 'Teslim alma başarısız');
    }
  };


  const handleZimmetDurumChange = async (zimmet, yeniDurum) => {
    // Aynı durum seçildiyse modal açma
    if (zimmet.durum === yeniDurum) {
      return;
    }
    
    // Modal'ı aç ve durum bilgilerini kaydet
    setDurumChangeData({
      zimmet: zimmet,
      yeniDurum: yeniDurum,
      aciklama: '',
      kullanici_adi: '',
      tamir_yeri: '',
      tahmini_sure: '',
      bozukluk_nedeni: '',
      kayip_tarihi: new Date().toISOString().split('T')[0],
      kayip_yeri: '',
      belge: []
    });
    setShowDurumModal(true);
  };

  const confirmDurumChange = async () => {
    const { zimmet, yeniDurum, aciklama, kullanici_adi, tamir_yeri, tahmini_sure, bozukluk_nedeni, kayip_tarihi, kayip_yeri, belge } = durumChangeData;
    
    setDurumBelgeUploading(true);
    
    // Demirbaş mı kontrol et
    const isDemirbas = zimmet.is_demirbas === true;
    
    // Önce belge(ler) varsa yükle (sadece normal zimmetler için)
    let belgeUrl = null;
    let belgeUrls = [];
    if (belge && belge.length > 0 && !isDemirbas) {
      try {
        for (const f of belge) {
          const formData = new FormData();
          formData.append('file', f);
          const uploadRes = await axios.post(`${API}/api/depot/zimmetler/${zimmet.id}/belge`, formData, {
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'multipart/form-data'
            }
          });
          // İlk belgenin url'ini geçmiş kaydı için sakla
          if (!belgeUrl) belgeUrl = uploadRes.data.url;
          belgeUrls.push({ url: uploadRes.data.url, dosya_adi: uploadRes.data.dosya_adi || f.name });
        }
      } catch (err) {
        console.error('Belge yükleme hatası:', err);
        toast.error('Belge yüklenemedi');
        setDurumBelgeUploading(false);
        return;
      }
    }
    
    // Kullanıcıda / Zimmette durumu için zimmet_ver endpoint'ini kullan (sadece normal zimmetler için)
    if ((yeniDurum === 'kullanicida' || yeniDurum === 'zimmette') && !isDemirbas) {
      if (!kullanici_adi) {
        toast.error('Kullanıcı adı gerekli');
        setDurumBelgeUploading(false);
        return;
      }
      try {
        const matchedUser = subeCalisanlari.find(c => c.name === kullanici_adi);
        await axios.post(`${API}/api/depot/zimmetler/${zimmet.id}/ver`, {
          kullanici_adi: kullanici_adi,
          kullanici_id: matchedUser ? matchedUser.id : null,
          durum: yeniDurum,
          aciklama: aciklama || '',
          belge_url: belgeUrl,
          belgeler: belgeUrls
        }, { headers: getAuthHeaders() });
        toast.success(yeniDurum === 'zimmette'
          ? `Ürün ${kullanici_adi} adlı kullanıcıya kalıcı olarak zimmetlendi`
          : `Ürün ${kullanici_adi} adlı kullanıcıya zimmetlendi`);
        setShowDurumModal(false);
        setDurumChangeData({ zimmet: null, yeniDurum: '', aciklama: '', kullanici_adi: '', tamir_yeri: '', tahmini_sure: '', bozukluk_nedeni: '', kayip_tarihi: '', kayip_yeri: '', belge: [] });
        setDurumBelgeUploading(false);
        fetchZimmetler();
        return;
      } catch (err) {
        toast.error('Zimmet verilemedi');
        setDurumBelgeUploading(false);
        return;
      }
    }

    // Build description based on status type
    let fullAciklama = aciklama;
    if (yeniDurum === 'tamirde' && tamir_yeri) {
      fullAciklama = `Tamir Yeri: ${tamir_yeri}${tahmini_sure ? `, Tahmini Süre: ${tahmini_sure}` : ''}${aciklama ? `. ${aciklama}` : ''}`;
    } else if (yeniDurum === 'bozuk' && bozukluk_nedeni) {
      fullAciklama = `Bozukluk Nedeni: ${bozukluk_nedeni}${aciklama ? `. ${aciklama}` : ''}`;
    } else if (yeniDurum === 'kayip') {
      fullAciklama = `Kayıp Tarihi: ${kayip_tarihi}${kayip_yeri ? `, Kayıp Yeri: ${kayip_yeri}` : ''}${aciklama ? `. ${aciklama}` : ''}`;
    }

    // Demirbaş ise purchasing/demirbaslar endpoint'ini kullan
    if (isDemirbas) {
      // Kullanıcıda/Zimmette durumu için kullanıcı adı kontrolü
      if ((yeniDurum === 'kullanicida' || yeniDurum === 'zimmette') && !kullanici_adi) {
        toast.error('Kullanıcı adı gerekli');
        setDurumBelgeUploading(false);
        return;
      }
      
      try {
        // Demirbaşlar için durum bilgisi oluştur
        let statusText = '';
        const matchedDemirbasUser = subeCalisanlari.find(c => c.name === kullanici_adi);
        if ((yeniDurum === 'kullanicida' || yeniDurum === 'zimmette') && kullanici_adi) {
          const prefix = yeniDurum === 'zimmette' ? 'Zimmette' : 'Kullanıcı';
          statusText = `${prefix}: ${kullanici_adi}${fullAciklama ? `. ${fullAciklama}` : ''}`;
        } else {
          statusText = fullAciklama || `Durum: ${yeniDurum}`;
        }

        // Yüklenen belgeleri multipart ile diske yükle (büyük dosyalar için güvenli)
        let demirbasDocs = [];
        if (belge && belge.length > 0) {
          for (const f of belge) {
            const formData = new FormData();
            formData.append('file', f);
            const upRes = await axios.post(`${API}/api/purchasing/demirbaslar/${zimmet.id}/belge`, formData, {
              headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
            });
            demirbasDocs.push({ url: upRes.data.url, dosya_adi: upRes.data.dosya_adi || f.name });
          }
        }
        
        // Demirbaşlar için purchasing endpoint'ini kullan
        const demirbasPayload = {
          last_status: statusText,
          last_status_description: statusText,
          usage_status: yeniDurum,
          current_user_name: kullanici_adi || null,
          last_status_documents: demirbasDocs
        };
        // Kişiye zimmetleniyorsa çalışanın onayına düşsün (pending)
        if ((yeniDurum === 'kullanicida' || yeniDurum === 'zimmette') && kullanici_adi) {
          demirbasPayload.current_user_id = matchedDemirbasUser ? matchedDemirbasUser.id : null;
          demirbasPayload.assignment_kabul_durumu = 'pending';
          demirbasPayload.assignment_tarihi = new Date().toISOString();
          demirbasPayload.assignment_aciklama = aciklama || '';
        }
        await axios.put(`${API}/api/purchasing/demirbaslar/${zimmet.id}`, demirbasPayload, { headers: getAuthHeaders() });
        
        const durumLabels = {
          depoda: 'Depoda',
          kullanicida: 'Kullanıcıda',
          zimmette: 'Zimmette',
          bozuk: 'Bozuk',
          tamirde: 'Tamirde',
          kayip: 'Kayıp',
          demirbas: 'Demirbaş'
        };
        toast.success(`Demirbaş durumu güncellendi: ${durumLabels[yeniDurum] || yeniDurum}`);
        setShowDurumModal(false);
        setDurumChangeData({ zimmet: null, yeniDurum: '', aciklama: '', kullanici_adi: '', tamir_yeri: '', tahmini_sure: '', bozukluk_nedeni: '', kayip_tarihi: '', kayip_yeri: '', belge: [] });
        setDurumBelgeUploading(false);
        fetchZimmetler();
        return;
      } catch (err) {
        console.error('Demirbaş durum güncelleme hatası:', err);
        toast.error('Demirbaş durumu güncellenemedi');
        setDurumBelgeUploading(false);
        return;
      }
    }

    // Normal zimmet için durum güncelle
    try {
      await axios.put(`${API}/api/depot/zimmetler/${zimmet.id}/durum`, { 
        durum: yeniDurum,
        aciklama: fullAciklama,
        belge_url: belgeUrl,
        belgeler: belgeUrls
      }, { headers: getAuthHeaders() });
      
      const durumLabels = {
        depoda: 'Depoda',
        kullanicida: 'Kullanıcıda',
        zimmette: 'Zimmette',
        bozuk: 'Bozuk',
        tamirde: 'Tamirde',
        kayip: 'Kayıp',
        demirbas: 'Demirbaş'
      };
      toast.success(`Durum güncellendi: ${durumLabels[yeniDurum]}`);
      setShowDurumModal(false);
      setDurumChangeData({ zimmet: null, yeniDurum: '', aciklama: '', kullanici_adi: '', tamir_yeri: '', tahmini_sure: '', bozukluk_nedeni: '', kayip_tarihi: '', kayip_yeri: '', belge: [] });
      setDurumBelgeUploading(false);
      fetchZimmetler();
    } catch (err) {
      console.error('Durum güncelleme hatası:', err);
      toast.error('Durum güncellenemedi');
      setDurumBelgeUploading(false);
    }
  };

  const getDurumModalTitle = (durum) => {
    switch (durum) {
      case 'depoda': return 'Depoya Al';
      case 'kullanicida': return 'Kullanıcıya Zimmetle';
      case 'zimmette': return 'Kişiye Zimmetle (Kalıcı)';
      case 'bozuk': return 'Bozuk Olarak İşaretle';
      case 'tamirde': return 'Tamire Gönder';
      case 'kayip': return 'Kayıp Olarak İşaretle';
      case 'demirbas': return 'Demirbaş Olarak İşaretle';
      default: return 'Durum Değiştir';
    }
  };

  const handleZimmetVer = async () => {
    if (!zimmetVerForm.kullanici_adi) {
      toast.error('Kullanıcı adı gerekli');
      return;
    }
    try {
      await axios.post(`${API}/api/depot/zimmetler/${selectedZimmet.id}/ver`, zimmetVerForm, { headers: getAuthHeaders() });
      toast.success(`Ürün ${zimmetVerForm.kullanici_adi} adlı kullanıcıya zimmetlendi`);
      setShowZimmetVerModal(false);
      setZimmetVerForm({ kullanici_adi: '', kullanici_id: '', aciklama: '' });
      setSelectedZimmet(null);
      fetchZimmetler();
    } catch (err) {
      toast.error('Zimmet verilemedi');
    }
  };

  const handleZimmetAl = async (zimmetId) => {
    try {
      await axios.post(`${API}/api/depot/zimmetler/${zimmetId}/al`, {}, { headers: getAuthHeaders() });
      toast.success('Zimmet geri alındı');
      fetchZimmetler();
    } catch (err) {
      toast.error('Zimmet geri alınamadı');
    }
  };

  const handleZimmetSil = async (zimmetId) => {
    if (!window.confirm('Bu zimmet kaydını silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/api/depot/zimmetler/${zimmetId}`, { headers: getAuthHeaders() });
      toast.success('Zimmet silindi');
      fetchZimmetler();
    } catch (err) {
      toast.error('Zimmet silinemedi');
    }
  };

  const handleShowGecmis = async (zimmet) => {
    setSelectedZimmet(zimmet);
    try {
      if (zimmet.is_demirbas) {
        // Demirbaş geçmişi purchasing_demirbaslar.status_history'de tutulur
        const res = await axios.get(`${API}/api/purchasing/demirbaslar/${zimmet.id}/history`, { headers: getAuthHeaders() });
        const normalized = (res.data || []).map((h, idx) => ({
          id: h.id || `dh-${idx}`,
          islem: 'demirbas_islem',
          islem_tarihi: h.date,
          islem_yapan_adi: h.user_name || '-',
          belgeler: (h.documents || []).map((d) => ({
            url: d.url || (d.data ? (String(d.data).startsWith('data:') ? d.data : `data:${d.type || 'application/octet-stream'};base64,${d.data}`) : d),
            dosya_adi: d.dosya_adi || d.name || 'Belge'
          })),
          _event: interpretDemirbasEvent(h)
        }));
        setZimmetGecmisi(normalized);
      } else {
        const res = await axios.get(`${API}/api/depot/zimmetler/${zimmet.id}/gecmis`, { headers: getAuthHeaders() });
        setZimmetGecmisi(res.data);
      }
      setShowGecmisModal(true);
    } catch (err) {
      toast.error('Geçmiş yüklenemedi');
    }
  };

  // Tutanak kişilerini (şubedeki tüm çalışanlar - FORTİS + taşeron) getir
  const fetchTutanakKisiler = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/depot/tutanak-kisiler`, { headers: getAuthHeaders() });
      setTutanakKisiler(res.data || []);
    } catch (err) {
      console.error('Tutanak kişileri yüklenemedi:', err);
    }
  }, []);

  const handleOpenTutanak = (zimmet) => {
    setTutanakForm({
      tutanak_turu: 'teslim_etme',
      teslim_eden: user?.name || '',
      teslim_alan: zimmet?.kullanici_adi || '',
      urun_adi: zimmet?.urun_adi || '',
      marka: zimmet?.marka || '',
      model: zimmet?.model || '',
      seri_no: zimmet?.seri_no || '',
      aciklama: '',
      iade_tarihi: '',
    });
    fetchTutanakKisiler();
    setTutanakModalOpen(true);
  };

  const handleDownloadTutanak = async () => {
    if (!tutanakForm.teslim_eden.trim() || !tutanakForm.teslim_alan.trim()) {
      toast.error('Teslim eden ve teslim alan alanları zorunludur');
      return;
    }
    setTutanakSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/api/depot/demirbas-tutanak-pdf`,
        { ...tutanakForm, branch_name: user?.branch_name || '' },
        { headers: getAuthHeaders(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const prefix = tutanakForm.tutanak_turu === 'teslim_alma' ? 'Teslim_Alma' : 'Teslim_Etme';
      link.download = `Demirbas_${prefix}_Tutanagi.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Tutanak oluşturuldu ve indiriliyor');
      setTutanakModalOpen(false);
    } catch (err) {
      console.error('Tutanak oluşturulamadı:', err);
      toast.error('Tutanak oluşturulamadı');
    } finally {
      setTutanakSubmitting(false);
    }
  };

  const filteredZimmetler = useMemo(() => {
    let result = zimmetler;
    
    // Arama filtresi
    if (zimmetSearchTerm) {
      const term = zimmetSearchTerm.toLowerCase();
      result = result.filter(z => 
        (z.urun_adi || '').toLowerCase().includes(term) ||
        (z.marka || '').toLowerCase().includes(term) ||
        (z.model || '').toLowerCase().includes(term) ||
        (z.seri_no || '').toLowerCase().includes(term) ||
        (z.kullanici_adi || '').toLowerCase().includes(term)
      );
    }
    
    // Sıralama
    if (zimmetSortConfig.key) {
      result = [...result].sort((a, b) => {
        let aVal = a[zimmetSortConfig.key] || '';
        let bVal = b[zimmetSortConfig.key] || '';
        
        if (zimmetSortConfig.key === 'created_at' || zimmetSortConfig.key === 'zimmet_tarihi') {
          aVal = new Date(aVal || 0).getTime();
          bVal = new Date(bVal || 0).getTime();
        } else {
          aVal = String(aVal).toLowerCase();
          bVal = String(bVal).toLowerCase();
        }
        
        if (aVal < bVal) return zimmetSortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return zimmetSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [zimmetler, zimmetSearchTerm, zimmetSortConfig]);

  const getDurumBadge = (durum) => {
    switch (durum) {
      case 'depoda':
        return <Badge className="bg-gray-100 text-gray-700 border-gray-300">Depoda</Badge>;
      case 'kullanicida':
        return <Badge className="bg-blue-100 text-blue-700 border-blue-300"><UserCheck className="h-3 w-3 mr-1" />Kullanıcıda</Badge>;
      case 'zimmette':
        return <Badge className="bg-indigo-100 text-indigo-700 border-indigo-300"><UserCheck className="h-3 w-3 mr-1" />Zimmette</Badge>;
      case 'bozuk':
        return <Badge className="bg-red-100 text-red-700 border-red-300"><AlertTriangle className="h-3 w-3 mr-1" />Bozuk</Badge>;
      case 'tamirde':
        return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300"><Wrench className="h-3 w-3 mr-1" />Tamirde</Badge>;
      case 'kayip':
        return <Badge className="bg-purple-100 text-purple-700 border-purple-300"><HelpCircle className="h-3 w-3 mr-1" />Kayıp</Badge>;
      case 'demirbas':
        return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300"><ClipboardList className="h-3 w-3 mr-1" />Demirbaş</Badge>;
      default:
        return <Badge>{durum}</Badge>;
    }
  };

  // Fetch stok kartları and stok durumu for add modal
  const fetchStokKartlari = useCallback(async () => {
    try {
      const [kartRes, stokRes] = await Promise.all([
        axios.get(`${API}/api/depot/stok-kartlari`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/depot/stok`, { headers: getAuthHeaders() })
      ]);
      setStokKartlari(kartRes.data);
      setStokDurumu(stokRes.data);
    } catch (err) {
      console.error('Stok kartları yüklenemedi:', err);
    }
  }, []);

  useEffect(() => {
    if (showAddModal) {
      fetchStokKartlari();
    }
  }, [showAddModal, fetchStokKartlari]);

  // Get available stock for a product
  const getAvailableStock = (stokKartiId) => {
    const stok = stokDurumu.find(s => s.stok_karti_id === stokKartiId);
    return stok ? stok.quantity : 0;
  };

  // Add item row
  const handleAddItem = () => {
    setAddForm(prev => ({
      ...prev,
      items: [...prev.items, { stok_karti_id: '', quantity: '', unit: 'adet' }]
    }));
  };

  // Remove item row
  const handleRemoveItem = (index) => {
    if (addForm.items.length === 1) return;
    setAddForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Update item
  const handleItemChange = (index, field, value) => {
    setAddForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  // Select stok kartı
  const handleSelectStokKarti = (index, stokKarti) => {
    setAddForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { 
        ...item, 
        stok_karti_id: stokKarti.id,
        unit: stokKarti.unit || 'adet'
      } : item)
    }));
    setStokKartiOpen(prev => ({ ...prev, [index]: false }));
  };

  // Submit stok çıkışı
  const handleSubmitStokCikisi = async () => {
    if (!addForm.requester_name.trim()) {
      toast.error('Talep eden kişi adı zorunludur');
      return;
    }

    if (!addForm.teslim_alan_id) {
      toast.error('Lütfen listeden geçerli bir Teslim Alan kişi seçiniz');
      return;
    }

    const validItems = addForm.items.filter(item => item.stok_karti_id && item.quantity > 0);
    if (validItems.length === 0) {
      toast.error('En az bir ürün ve miktar girmelisiniz');
      return;
    }

    // Check stock availability
    for (const item of validItems) {
      const available = getAvailableStock(item.stok_karti_id);
      if (parseFloat(item.quantity) > available) {
        const kart = stokKartlari.find(k => k.id === item.stok_karti_id);
        toast.error(`Yetersiz stok: ${kart?.product_name}. Mevcut: ${available}`);
        return;
      }
    }

    try {
      setSubmitting(true);
      await axios.post(`${API}/api/depot/stok-cikisleri`, {
        requester_name: addForm.requester_name,
        taseron_firma: addForm.taseron_firma,
        teslim_alan: addForm.teslim_alan,
        teslim_alan_id: addForm.teslim_alan_id || '',
        receiver_name: addForm.teslim_alan,
        receiver_id: addForm.teslim_alan_id || '',
        description: addForm.description,
        items: validItems.map(item => ({
          stok_karti_id: item.stok_karti_id,
          quantity: parseFloat(item.quantity),
          unit: item.unit
        }))
      }, { headers: getAuthHeaders() });

      toast.success('Stok çıkışı oluşturuldu');
      setShowAddModal(false);
      setAddForm({
        requester_name: '',
        taseron_firma: '',
        teslim_alan: '',
        teslim_alan_id: '',
        description: '',
        items: [{ stok_karti_id: '', quantity: '', unit: 'adet' }]
      });
      fetchStokCikisleri();
    } catch (err) {
      console.error('Stok çıkışı hatası:', err);
      const errorMsg = err.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg || e).join(', '));
      } else {
        toast.error('Stok çıkışı oluşturulamadı');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Silme işlemi
  const handleDeleteStokCikisi = (id) => {
    requestPasswordForAction('deleteStokCikisi', id);
  };

  const executeDeleteStokCikisi = async (id) => {
    try {
      await axios.delete(`${API}/api/depot/stok-cikisleri/${id}`, { headers: getAuthHeaders() });
      toast.success('Stok çıkışı silindi');
      fetchStokCikisleri();
    } catch (err) {
      toast.error('Stok çıkışı silinemedi');
    }
  };

  // Teslim tutanağı yükleme
  const handleUploadTutanak = async (cikisId, file) => {
    if (!file) return;
    
    setUploadProgress(true);
    setUploadingCikisId(cikisId);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Doğrudan teslim belgesi endpoint'ine yükle
      await axios.post(`${API}/api/depot/stok-cikisleri/${cikisId}/teslim-belgesi`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Teslim tutanağı yüklendi');
      fetchStokCikisleri();
    } catch (err) {
      console.error('Upload error:', err);
      const errorMsg = err.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg || e).join(', '));
      } else {
        toast.error('Belge yüklenemedi');
      }
    } finally {
      setUploadProgress(false);
      setUploadingCikisId(null);
    }
  };

  // Get unique values for dropdowns
  const uniqueValues = useMemo(() => {
    const requesters = [...new Set(stokCikisleri.map(c => c.requester_name).filter(Boolean))];
    const products = [...new Set(stokCikisleri.flatMap(c => c.items?.map(i => i.product_name) || []).filter(Boolean))];
    const taseronFirmalar = [...new Set(stokCikisleri.map(c => c.taseron_firma).filter(Boolean))];
    const createdByList = [...new Set(stokCikisleri.map(c => c.created_by_name).filter(Boolean))];
    return { requesters, products, taseronFirmalar, createdByList };
  }, [stokCikisleri]);

  // Filter logic
  const filteredCikislar = useMemo(() => {
    return stokCikisleri.filter(cikis => {
      // Reddedilen çıkışları listede gösterme
      if (cikis.approval_status === 'rejected') return false;
      // Date filter
      if (filters.dateFrom) {
        const cikisDate = new Date(cikis.created_at).setHours(0,0,0,0);
        const fromDate = new Date(filters.dateFrom).setHours(0,0,0,0);
        if (cikisDate < fromDate) return false;
      }
      if (filters.dateTo) {
        const cikisDate = new Date(cikis.created_at).setHours(0,0,0,0);
        const toDate = new Date(filters.dateTo).setHours(0,0,0,0);
        if (cikisDate > toDate) return false;
      }
      
      // Requester filter
      if (filters.requester && cikis.requester_name !== filters.requester) return false;
      
      // Product filter
      if (filters.product) {
        const hasProduct = cikis.items?.some(item => item.product_name === filters.product);
        if (!hasProduct) return false;
      }
      
      // Taşeron firma filter
      if (filters.taseronFirma && cikis.taseron_firma !== filters.taseronFirma) return false;
      
      // Created by filter
      if (filters.createdBy && cikis.created_by_name !== filters.createdBy) return false;
      
      // Search term filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesSearch = 
          (cikis.requester_name || '').toLowerCase().includes(term) ||
          (cikis.taseron_firma || '').toLowerCase().includes(term) ||
          (cikis.description || '').toLowerCase().includes(term) ||
          (cikis.created_by_name || '').toLowerCase().includes(term) ||
          cikis.items?.some(item => (item.product_name || '').toLowerCase().includes(term));
        if (!matchesSearch) return false;
      }
      
      return true;
    });

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'created_at':
          aVal = new Date(a.created_at);
          bVal = new Date(b.created_at);
          break;
        case 'requester_name':
          aVal = (a.requester_name || '').toLowerCase();
          bVal = (b.requester_name || '').toLowerCase();
          break;
        case 'taseron_firma':
          aVal = (a.taseron_firma || '').toLowerCase();
          bVal = (b.taseron_firma || '').toLowerCase();
          break;
        case 'product':
          aVal = (a.items?.[0]?.product_name || '').toLowerCase();
          bVal = (b.items?.[0]?.product_name || '').toLowerCase();
          break;
        case 'quantity':
          aVal = a.items?.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0) || 0;
          bVal = b.items?.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0) || 0;
          break;
        case 'delivery_status':
          const statusOrder = { pending: 0, confirmed: 1, rejected: 2 };
          aVal = statusOrder[a.delivery_status] ?? 3;
          bVal = statusOrder[b.delivery_status] ?? 3;
          break;
        default:
          aVal = a[sortConfig.key];
          bVal = b[sortConfig.key];
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [stokCikisleri, filters, searchTerm, sortConfig]);

  // Handle sort
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="h-4 w-4 ml-1 text-gray-400" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1 text-emerald-600" />
      : <ArrowDown className="h-4 w-4 ml-1 text-emerald-600" />;
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      requester: '',
      product: '',
      taseronFirma: '',
      createdBy: ''
    });
  };

  // Check if any filter is active
  const hasActiveFilters = Object.values(filters).some(v => v !== '');
  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  if (loading) {
    return (
      <div className="space-y-6" data-testid="depot-exit-loading">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Stok Çıkış</h1>
            <p className="text-gray-500">Stok çıkış geçmişi</p>
          </div>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PackageMinus className="h-5 w-5 text-emerald-600" />
              Stok Çıkışları
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6" data-testid="depot-exit-error">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Stok Çıkış</h1>
            <p className="text-gray-500">Stok çıkış geçmişi</p>
          </div>
        </div>
        <Card>
          <CardContent className="py-12">
            <div className="flex flex-col items-center justify-center text-red-500">
              <PackageMinus className="h-12 w-12 mb-4" />
              <p>{error}</p>
              <button 
                onClick={fetchStokCikisleri}
                className="mt-4 text-emerald-600 hover:text-emerald-700 underline"
              >
                Tekrar Dene
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Bilgilendirme kutusunu kapat ve localStorage'a kaydet
  const hideStokCikisInfo = () => {
    setShowStokCikisInfo(false);
    localStorage.setItem('depot_stok_cikis_info_hidden', 'true');
  };

  const hideZimmetInfo = () => {
    setShowZimmetInfo(false);
    localStorage.setItem('depot_zimmet_info_hidden', 'true');
  };

  // Excel Export Function for Stok Çıkışları
  const exportStokCikisleriToExcel = () => {
    if (filteredCikislar.length === 0) {
      toast.error('İndirilecek veri bulunmuyor');
      return;
    }

    // Build rows from filtered data
    const rows = [];
    filteredCikislar.forEach(cikis => {
      (cikis.items || []).forEach(item => {
        rows.push({
          tarih: new Date(cikis.created_at).toLocaleDateString('tr-TR'),
          saat: new Date(cikis.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
          talepEden: cikis.requester_name || '-',
          teslimAlan: cikis.teslim_alan || '-',
          taseronFirma: cikis.taseron_firma || '-',
          urunAdi: item.product_name || '-',
          miktar: item.quantity || 0,
          birim: item.unit || 'adet',
          olusturan: cikis.created_by_name || '-',
          aciklama: (cikis.description || '-').replace(/;/g, ',').replace(/\n/g, ' ')
        });
      });
    });

    // Build CSV content (Excel compatible with Turkish encoding)
    const BOM = '\uFEFF'; // UTF-8 BOM for Turkish characters
    const headers = ['Tarih', 'Saat', 'Talep Eden', 'Teslim Alan', 'Taşeron Firma', 'Ürün Adı', 'Miktar', 'Birim', 'Oluşturan', 'Açıklama'];
    const csvContent = BOM + [
      headers.join(';'),
      ...rows.map(row => [
        row.tarih,
        row.saat,
        row.talepEden,
        row.teslimAlan,
        row.taseronFirma,
        row.urunAdi,
        row.miktar,
        row.birim,
        row.olusturan,
        row.aciklama
      ].join(';'))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    // Generate filename with date range if filters applied
    let dateRangeStr = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
    if (filters.dateFrom && filters.dateTo) {
      dateRangeStr = `${filters.dateFrom}_${filters.dateTo}`;
    } else if (filters.dateFrom) {
      dateRangeStr = `${filters.dateFrom}_sonrasi`;
    } else if (filters.dateTo) {
      dateRangeStr = `${filters.dateTo}_oncesi`;
    }
    
    const fileName = `Stok_Cikislari_${dateRangeStr}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Excel dosyası indirildi');
  };

  // Excel Export Function for Zimmetler
  const exportZimmetlerToExcel = () => {
    if (filteredZimmetler.length === 0) {
      toast.error('İndirilecek veri bulunmuyor');
      return;
    }

    const rows = filteredZimmetler.map(zimmet => ({
      tarih: new Date(zimmet.created_at).toLocaleDateString('tr-TR'),
      saat: new Date(zimmet.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
      urunAdi: zimmet.urun_adi || '-',
      marka: zimmet.marka || '-',
      model: zimmet.model || '-',
      seriNo: zimmet.seri_no || '-',
      durum: zimmet.durum || 'Depoda',
      zimmetliKisi: zimmet.zimmetli_kisi || '-',
      aciklama: (zimmet.aciklama || '-').replace(/;/g, ',').replace(/\n/g, ' ')
    }));

    // Build CSV content
    const BOM = '\uFEFF';
    const headers = ['Tarih', 'Saat', 'Ürün Adı', 'Marka', 'Model', 'Seri No', 'Durum', 'Zimmetli Kişi', 'Açıklama'];
    const csvContent = BOM + [
      headers.join(';'),
      ...rows.map(row => [
        row.tarih,
        row.saat,
        row.urunAdi,
        row.marka,
        row.model,
        row.seriNo,
        row.durum,
        row.zimmetliKisi,
        row.aciklama
      ].join(';'))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const fileName = `Zimmetler_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Excel dosyası indirildi');
  };

  return (
    <div className="space-y-6" data-testid="depot-exit">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stok Çıkış & Zimmetler</h1>
          <p className="text-gray-500">Stok çıkış geçmişi ve zimmet takibi</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 max-w-3xl">
          <TabsTrigger value="stok-cikis" className="gap-2">
            <PackageMinus className="h-4 w-4" />
            Stok Çıkış
          </TabsTrigger>
          <TabsTrigger value="zimmetler" className="gap-2">
            <UserCheck className="h-4 w-4" />
            Zimmetler - Demirbaşlar
          </TabsTrigger>
          <TabsTrigger value="taseronlar" className="gap-2">
            <Building2 className="h-4 w-4" />
            Taşeronlar
          </TabsTrigger>
          <TabsTrigger value="teslim-alanlar" className="gap-2">
            <User className="h-4 w-4" />
            Teslim Alanlar
          </TabsTrigger>
        </TabsList>

        <TabsContent value="stok-cikis">
      <Card>
        <CardHeader className="flex flex-col gap-4">
          <div className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <PackageMinus className="h-5 w-5 text-emerald-600" />
              Stok Çıkışları ({filteredCikislar.length}/{stokCikisleri.length})
            </CardTitle>
            <div className="flex items-center gap-2">
              {/* Excel İndir Butonu */}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={exportStokCikisleriToExcel}
                className="gap-1 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
              >
                <Download className="h-4 w-4" />
                Excel İndir
              </Button>
              {/* Arama Çubuğu */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Ara... (ürün, firma, talep eden)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-64 h-9"
                  data-testid="search-input"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <Button
                variant={showFilters ? "default" : "outline"}
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? "bg-emerald-600 hover:bg-emerald-700" : ""}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filtrele
                {activeFilterCount > 0 && (
                  <Badge className="ml-2 bg-white text-emerald-600">{activeFilterCount}</Badge>
                )}
              </Button>
              {(hasActiveFilters || searchTerm) && (
                <Button variant="ghost" size="sm" onClick={() => { clearFilters(); setSearchTerm(''); }} className="text-red-500 hover:text-red-700">
                  <X className="h-4 w-4 mr-1" />
                  Temizle
                </Button>
              )}
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 p-4 bg-gray-50 rounded-lg border">
              {/* Date From */}
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Başlangıç Tarihi</Label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                  className="h-9"
                />
              </div>
              
              {/* Date To */}
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Bitiş Tarihi</Label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                  className="h-9"
                />
              </div>
              
              {/* Requester */}
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Talep Eden</Label>
                <Select value={filters.requester} onValueChange={(val) => setFilters(prev => ({ ...prev, requester: val === 'all' ? '' : val }))}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Tümü" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    {uniqueValues.requesters.map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Product */}
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Ürün</Label>
                <Select value={filters.product} onValueChange={(val) => setFilters(prev => ({ ...prev, product: val === 'all' ? '' : val }))}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Tümü" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    {uniqueValues.products.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Taşeron Firma */}
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Firma</Label>
                <Select value={filters.taseronFirma} onValueChange={(val) => setFilters(prev => ({ ...prev, taseronFirma: val === 'all' ? '' : val }))}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Tümü" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    {uniqueValues.taseronFirmalar.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Created By */}
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Oluşturan</Label>
                <Select value={filters.createdBy} onValueChange={(val) => setFilters(prev => ({ ...prev, createdBy: val === 'all' ? '' : val }))}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Tümü" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    {uniqueValues.createdByList.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          {/* Kapanabilir Bilgilendirme Kutusu */}
          {showStokCikisInfo && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg relative">
              <button 
                onClick={hideStokCikisInfo}
                className="absolute top-2 right-2 p-1 hover:bg-blue-100 rounded-full transition-colors"
                aria-label="Bilgilendirmeyi kapat"
              >
                <X className="h-4 w-4 text-blue-600" />
              </button>
              <div className="flex gap-3 pr-6">
                <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">
                  Stok Çıkış bölümünde size gelen istekler doğrultusunda yapılan ürün teslimatlarının detaylarına ve stoktan çıkışların listesi bulunmaktadır. 
                  Bu listedeki kontrollerinizi buradan kolayca sağlayabilirsiniz.
                </p>
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {(() => {
            const reddedilenler = stokCikisleri.filter(c => c.approval_status === 'rejected' && !c.is_archived);
            if (reddedilenler.length === 0) return null;
            return (
              <div className="mb-6 rounded-lg border border-red-200 bg-red-50/60 p-4" data-testid="rejected-exits-section">
                <div className="flex items-center gap-2 mb-3">
                  <PackageMinus className="h-5 w-5 text-red-500" />
                  <h3 className="font-semibold text-red-700">Reddedilen Çıkışlar ({reddedilenler.length})</h3>
                </div>
                <div className="space-y-2">
                  {reddedilenler.map((cikis) => (
                    <div key={cikis.id} data-testid={`rejected-exit-${cikis.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-red-200 bg-white px-3 py-2">
                      <div className="flex flex-col text-sm">
                        <span className="font-medium text-gray-800">{cikis.teslim_alan || '-'}</span>
                        <span className="text-gray-600">
                          {cikis.items?.map(i => `${i.product_name} (${i.quantity} ${i.unit || ''})`).join(', ')}
                        </span>
                        {cikis.rejection_reason && (
                          <span className="text-xs text-red-600 mt-0.5">Red sebebi: {cikis.rejection_reason}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          data-testid={`tekrar-aktif-btn-${cikis.id}`}
                          onClick={() => handleTekrarAktifEt(cikis.id)}
                        >
                          Tekrar Aktif Et
                        </Button>
                        <Button
                          size="sm"
                          className="bg-gray-700 hover:bg-gray-800 text-white"
                          data-testid={`tamamla-btn-${cikis.id}`}
                          onClick={() => handleTamamlaReddedilen(cikis.id)}
                        >
                          Tamamla
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {filteredCikislar.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <PackageMinus className="h-12 w-12 mb-4 text-gray-300" />
              <p>{hasActiveFilters ? 'Filtrelere uygun kayıt bulunamadı' : 'Henüz stok çıkışı bulunmuyor'}</p>
              {hasActiveFilters && (
                <Button variant="link" onClick={clearFilters} className="mt-2 text-emerald-600">
                  Filtreleri temizle
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center">
                      Tarih
                      {getSortIcon('created_at')}
                    </div>
                  </TableHead>
                  <TableHead>Tip</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('requester_name')}
                  >
                    <div className="flex items-center">
                      Talep Eden
                      {getSortIcon('requester_name')}
                    </div>
                  </TableHead>
                  <TableHead>Teslim Alan</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('product')}
                  >
                    <div className="flex items-center">
                      Ürünler
                      {getSortIcon('product')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('quantity')}
                  >
                    <div className="flex items-center">
                      Miktar
                      {getSortIcon('quantity')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('taseron_firma')}
                  >
                    <div className="flex items-center">
                      Firma
                      {getSortIcon('taseron_firma')}
                    </div>
                  </TableHead>
                  <TableHead>Teslim Belgeleri</TableHead>
                  <TableHead>Açıklama</TableHead>
                  <TableHead>Teslim Tutanağı</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCikislar.map((cikis) => (
                  <TableRow key={cikis.id} data-testid={`exit-row-${cikis.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <div className="flex flex-col">
                          <span>{new Date(cikis.created_at).toLocaleDateString('tr-TR')}</span>
                          <span className="text-xs text-gray-500">{new Date(cikis.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {cikis.exit_type === 'transfer' ? (
                        <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                          <ArrowUpDown className="h-3 w-3 mr-1" />
                          Transfer
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-gray-500">
                          Normal
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        {cikis.requester_name || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-blue-400" />
                        {cikis.teslim_alan || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {cikis.items?.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <Package className="h-3 w-3 text-emerald-500" />
                            <span className="font-medium">{item.product_name}</span>
                          </div>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {cikis.items?.map((item, idx) => (
                          <div key={idx} className="text-sm font-medium text-emerald-600">
                            {item.quantity} {item.unit}
                          </div>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {cikis.taseron_firma ? (
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-blue-500" />
                          <span className="text-sm font-medium text-blue-600">{cikis.taseron_firma}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {cikis.exit_type === 'transfer' && (cikis.transfer_teslim_alan_belgesi || cikis.transfer_teslim_eden_belgesi) ? (
                        <div className="space-y-1">
                          {cikis.transfer_teslim_alan_belgesi && (
                            <a
                              href={`${API}${cikis.transfer_teslim_alan_belgesi.url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline bg-blue-50 px-2 py-1 rounded"
                            >
                              <Download className="h-3 w-3" />
                              Teslim Alan
                            </a>
                          )}
                          {cikis.transfer_teslim_eden_belgesi && (
                            <a
                              href={`${API}${cikis.transfer_teslim_eden_belgesi.url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs text-green-600 hover:text-green-800 hover:underline bg-green-50 px-2 py-1 rounded"
                            >
                              <Download className="h-3 w-3" />
                              Teslim Eden
                            </a>
                          )}
                        </div>
                      ) : cikis.teslim_belgeleri?.length > 0 ? (
                        <div className="space-y-1">
                          {cikis.teslim_belgeleri.map((doc, idx) => {
                            // URL'yi düzelt - /api/uploads formatında olmalı
                            let docUrl = doc.url || doc.data;
                            if (docUrl && docUrl.startsWith('/uploads/')) {
                              docUrl = `${API}/api${docUrl.substring(0)}`;
                            } else if (docUrl && docUrl.startsWith('/api/uploads/')) {
                              docUrl = `${API}${docUrl}`;
                            } else if (docUrl && !docUrl.startsWith('http')) {
                              docUrl = `${API}${docUrl}`;
                            }
                            return (
                              <div key={idx} className="flex items-center gap-1 bg-green-50 border border-green-200 rounded px-2 py-1">
                                <FileText className="h-3 w-3 text-green-600" />
                                <span className="text-xs text-green-700 truncate max-w-[80px]" title={doc.name}>{doc.name}</span>
                                <div className="flex gap-1 ml-1">
                                  {docUrl && (
                                    <>
                                      <a 
                                        href={docUrl} 
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-500 hover:text-blue-700"
                                        title="Görüntüle"
                                      >
                                        <Eye className="h-3 w-3" />
                                      </a>
                                      <a 
                                        href={docUrl} 
                                        download={doc.name}
                                        className="text-emerald-500 hover:text-emerald-700"
                                        title="İndir"
                                      >
                                        <Download className="h-3 w-3" />
                                      </a>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {cikis.description || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-2">
                        {/* Tutanak İndir ve Yükle Butonları */}
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = `${API}/api/depot/stok-cikisleri/${cikis.id}/teslim-tutanagi-pdf`;
                              link.download = `Teslim_Tutanagi_${cikis.id.slice(0, 8).toUpperCase()}.pdf`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 text-xs h-7"
                            data-testid="download-tutanak-btn"
                          >
                            <Download className="h-3 w-3 mr-1" />
                            İndir
                          </Button>
                          {/* Yükle Butonu */}
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files?.[0]) {
                                  handleUploadTutanak(cikis.id, e.target.files[0]);
                                  e.target.value = '';
                                }
                              }}
                              disabled={uploadProgress && uploadingCikisId === cikis.id}
                            />
                            <div className={cn(
                              "flex items-center justify-center h-7 w-7 rounded border text-xs",
                              uploadProgress && uploadingCikisId === cikis.id
                                ? "bg-gray-100 border-gray-300 cursor-not-allowed"
                                : "bg-blue-50 border-blue-300 text-blue-600 hover:bg-blue-100"
                            )}>
                              {uploadProgress && uploadingCikisId === cikis.id ? (
                                <div className="animate-spin h-3 w-3 border-2 border-blue-600 border-t-transparent rounded-full" />
                              ) : (
                                <Plus className="h-4 w-4" />
                              )}
                            </div>
                          </label>
                        </div>
                        {/* Yüklenen Tutanaklar */}
                        {cikis.teslim_tutanaklari?.length > 0 && (
                          <div className="space-y-1">
                            {cikis.teslim_tutanaklari.map((doc, idx) => (
                              <div key={idx} className="flex items-center gap-1 bg-green-50 border border-green-200 rounded px-2 py-1">
                                <FileText className="h-3 w-3 text-green-600" />
                                <span className="text-xs text-green-700 truncate max-w-[80px]" title={doc.name}>
                                  {doc.name}
                                </span>
                                {doc.url && (
                                  <a 
                                    href={doc.url.startsWith('http') ? doc.url : `${API}${doc.url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-500 hover:text-blue-700 ml-auto"
                                    title="Görüntüle/İndir"
                                  >
                                    <Download className="h-3 w-3" />
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Stok Çıkış Ekle Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageMinus className="h-5 w-5 text-emerald-600" />
              Yeni Stok Çıkışı
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Talep Eden ve Teslim Alan */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Talep Eden *</Label>
                <Input
                  value={addForm.requester_name}
                  onChange={(e) => setAddForm(prev => ({ ...prev, requester_name: e.target.value }))}
                  placeholder="Talep eden kişi adı..."
                  data-testid="requester-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Teslim Alan *</Label>
                <Input
                  list="teslim-alan-list"
                  value={addForm.teslim_alan || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    const matched = subeCalisanlari.find(c => c.name === val);
                    setAddForm(prev => ({ ...prev, teslim_alan: val, teslim_alan_id: matched ? matched.id : '' }));
                  }}
                  placeholder="Teslim alan kişiyi listeden seçin..."
                  data-testid="teslim-alan-input"
                />
                <datalist id="teslim-alan-list">
                  {subeCalisanlari.map((c) => (
                    <option key={c.id} value={c.name} />
                  ))}
                </datalist>
                {addForm.teslim_alan && !addForm.teslim_alan_id && (
                  <p className="text-xs text-red-600 font-semibold">Geçerli bir kullanıcı seçilmedi. Çıkış yapabilmek için listeden seçim yapmalısınız.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Firma</Label>
                <Input
                  value={addForm.taseron_firma}
                  onChange={(e) => setAddForm(prev => ({ ...prev, taseron_firma: e.target.value }))}
                  placeholder="Firma seçin..."
                />
              </div>
            </div>

            {/* Ürünler */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Ürünler *</Label>
                <Button type="button" variant="outline" size="sm" onClick={handleAddItem} className="gap-1">
                  <Plus className="h-4 w-4" />
                  Ürün Ekle
                </Button>
              </div>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {addForm.items.map((item, index) => {
                  const selectedKart = stokKartlari.find(k => k.id === item.stok_karti_id);
                  const available = getAvailableStock(item.stok_karti_id);
                  return (
                    <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border">
                      {/* Ürün Seçimi */}
                      <div className="flex-1">
                        <Input
                          list={`stok-karti-list-${index}`}
                          value={selectedKart ? selectedKart.product_name : (item._typed || '')}
                          onChange={(e) => {
                            const val = e.target.value;
                            const matched = stokKartlari.find(k => k.product_name === val);
                            if (matched) {
                              handleSelectStokKarti(index, matched);
                            } else {
                              setAddForm(prev => ({
                                ...prev,
                                items: prev.items.map((it, i) => i === index ? { ...it, stok_karti_id: '', _typed: val } : it)
                              }));
                            }
                          }}
                          placeholder="Ürün seçin veya yazın..."
                          className="h-9 w-full"
                          data-testid={`product-select-${index}`}
                        />
                        <datalist id={`stok-karti-list-${index}`}>
                          {stokKartlari.filter(kart => kart.product_name && kart.product_name.trim()).map((kart) => {
                            const stok = getAvailableStock(kart.id);
                            return (
                              <option key={kart.id} value={kart.product_name}>
                                {`Stok: ${stok} ${kart.unit || 'adet'}`}
                              </option>
                            );
                          })}
                        </datalist>
                        {selectedKart && (
                          <p className="text-xs text-gray-500 mt-1">
                            Mevcut stok: <span className={available > 0 ? "text-green-600 font-medium" : "text-red-600 font-medium"}>{available} {selectedKart.unit || 'adet'}</span>
                          </p>
                        )}
                      </div>

                      {/* Miktar */}
                      <div className="w-24">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          placeholder="Miktar"
                          className="h-9"
                          data-testid={`quantity-input-${index}`}
                        />
                      </div>

                      {/* Birim */}
                      <div className="w-20">
                        <Input
                          value={item.unit}
                          onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                          placeholder="Birim"
                          className="h-9 text-center"
                          disabled
                        />
                      </div>

                      {/* Sil */}
                      {addForm.items.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveItem(index)}
                          className="text-red-500 hover:text-red-700 h-9 w-9 p-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Açıklama */}
            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Textarea
                value={addForm.description}
                onChange={(e) => setAddForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Çıkış açıklaması (opsiyonel)..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)} disabled={submitting}>
              İptal
            </Button>
            <Button 
              onClick={handleSubmitStokCikisi} 
              className="bg-emerald-600 hover:bg-emerald-700" 
              disabled={submitting}
              data-testid="submit-stok-cikis-btn"
            >
              {submitting ? 'Kaydediliyor...' : 'Stok Çıkışı Oluştur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Şifre Doğrulama Modalı */}
      <Dialog open={passwordModal} onOpenChange={setPasswordModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-amber-600" />
              Şifre Doğrulama
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Bu işlemi gerçekleştirmek için şifre girmeniz gerekmektedir.
            </p>
            <div>
              <Label>Şifre *</Label>
              <Input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="Şifreyi girin..."
                onKeyDown={(e) => e.key === 'Enter' && handlePasswordConfirm()}
                data-testid="password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordModal(false)}>İptal</Button>
            <Button onClick={handlePasswordConfirm} className="bg-amber-600 hover:bg-amber-700" data-testid="password-confirm-btn">
              Onayla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </TabsContent>

        {/* ZİMMETLER SEKMESİ */}
        <TabsContent value="zimmetler">
          <Card>
            <CardHeader className="flex flex-col gap-4">
              <div className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <UserCheck className="h-5 w-5 text-blue-600" />
                  Zimmetler ({filteredZimmetler.length})
                </CardTitle>
                <div className="flex items-center gap-2">
                  {/* Excel İndir Butonu */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={exportZimmetlerToExcel}
                    className="gap-1 text-blue-700 border-blue-300 hover:bg-blue-50"
                  >
                    <Download className="h-4 w-4" />
                    Excel İndir
                  </Button>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Ara... (ürün, marka, seri no)"
                      value={zimmetSearchTerm}
                      onChange={(e) => setZimmetSearchTerm(e.target.value)}
                      className="pl-9 w-64 h-9"
                    />
                  </div>
{/* Ürün Ekle butonu kaldırıldı - ürünler Satın Alma'dan gelecek */}
                </div>
              </div>
              
              {/* Kapanabilir Bilgilendirme Kutusu */}
              {showZimmetInfo && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg relative">
                  <button 
                    onClick={hideZimmetInfo}
                    className="absolute top-2 right-2 p-1 hover:bg-blue-100 rounded-full transition-colors"
                    aria-label="Bilgilendirmeyi kapat"
                  >
                    <X className="h-4 w-4 text-blue-600" />
                  </button>
                  <div className="flex gap-3 pr-6">
                    <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-800">
                      Zimmetler bölümünde stoğunuza girmiş demirbaşların hareketlerini buradan kayıt altına alabilir ve kontrollerinizi yapabilirsiniz.
                    </p>
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {zimmetLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : filteredZimmetler.length === 0 ? (
                <div className="text-center py-10 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Henüz zimmet/demirbaş kaydı bulunmuyor</p>
                  <p className="text-sm mt-2">Satın Alma modülünden eklenen demirbaşlar burada görüntülenecek</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-100"
                        onClick={() => handleZimmetSort('created_at')}
                      >
                        <div className="flex items-center">Tarih {getZimmetSortIcon('created_at')}</div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-100"
                        onClick={() => handleZimmetSort('urun_adi')}
                      >
                        <div className="flex items-center">Ürün Adı {getZimmetSortIcon('urun_adi')}</div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-100"
                        onClick={() => handleZimmetSort('marka')}
                      >
                        <div className="flex items-center">Marka {getZimmetSortIcon('marka')}</div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-100"
                        onClick={() => handleZimmetSort('model')}
                      >
                        <div className="flex items-center">Model {getZimmetSortIcon('model')}</div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-100"
                        onClick={() => handleZimmetSort('seri_no')}
                      >
                        <div className="flex items-center">Seri No {getZimmetSortIcon('seri_no')}</div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-100"
                        onClick={() => handleZimmetSort('durum')}
                      >
                        <div className="flex items-center">Durum {getZimmetSortIcon('durum')}</div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-100"
                        onClick={() => handleZimmetSort('kullanici_adi')}
                      >
                        <div className="flex items-center">Kullanıcı {getZimmetSortIcon('kullanici_adi')}</div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-100"
                        onClick={() => handleZimmetSort('kabul_durumu')}
                      >
                        <div className="flex items-center">Yanıt Durumu {getZimmetSortIcon('kabul_durumu')}</div>
                      </TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredZimmetler.map((zimmet) => (
                      <TableRow key={zimmet.id}>
                        <TableCell>
                          <div className="flex flex-col">
                            <span>{new Date(zimmet.created_at).toLocaleDateString('tr-TR')}</span>
                            <span className="text-xs text-gray-500">{new Date(zimmet.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {zimmet.urun_adi || '-'}
                          {zimmet.is_demirbas && zimmet.depot_receipt_status === 'pending' && (
                            <div className="mt-1" data-testid={`demirbas-pending-${zimmet.id}`}>
                              <Badge className="bg-orange-100 text-orange-700 border-orange-300 text-xs whitespace-normal">
                                <AlertTriangle className="h-3 w-3 mr-1 inline" />
                                Ürün teslim alınmadı — Konum: {zimmet.pending_location || zimmet.location || '-'}
                              </Badge>
                            </div>
                          )}
                          {zimmet.is_demirbas && zimmet.depot_receipt_status === 'received' && (
                            <div className="mt-1">
                              <Badge className="bg-green-100 text-green-700 border-green-300 text-xs">
                                <CheckCircle className="h-3 w-3 mr-1 inline" />
                                Teslim Alındı{zimmet.depot_received_by_name ? ` — ${zimmet.depot_received_by_name}` : ''}
                              </Badge>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>{zimmet.marka || '-'}</TableCell>
                        <TableCell>{zimmet.model || '-'}</TableCell>
                        <TableCell className="font-mono text-sm">{zimmet.seri_no || '-'}</TableCell>
                        <TableCell>
                          <Select
                            value={zimmet.durum}
                            onValueChange={(val) => handleZimmetDurumChange(zimmet, val)}
                          >
                            <SelectTrigger className="w-36 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="depoda">Depoda</SelectItem>
                              <SelectItem value="kullanicida">Kullanıcıda</SelectItem>
                              <SelectItem value="zimmette">Zimmette</SelectItem>
                              <SelectItem value="bozuk">Bozuk</SelectItem>
                              <SelectItem value="tamirde">Tamirde</SelectItem>
                              <SelectItem value="kayip">Kayıp</SelectItem>
                              <SelectItem value="demirbas">Demirbaş</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          {zimmet.kullanici_adi ? (
                            <div className="flex items-center gap-1">
                              <UserCheck className="h-4 w-4 text-blue-600" />
                              <span className="text-blue-700 font-medium">{zimmet.kullanici_adi}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {(zimmet.durum === 'kullanicida' || zimmet.durum === 'zimmette') ? (
                            zimmet.kabul_durumu === 'accepted' ? (
                              <Badge className="bg-green-100 text-green-700">Teslim Aldı</Badge>
                            ) : zimmet.kabul_durumu === 'rejected' ? (
                              <Badge className="bg-red-100 text-red-700">Reddetti</Badge>
                            ) : (
                              <Badge className="bg-yellow-100 text-yellow-700">Yanıt Vermedi</Badge>
                            )
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {zimmet.is_demirbas && zimmet.depot_receipt_status === 'pending' && (
                              <Button
                                size="sm"
                                onClick={() => handleDemirbasTeslimAl(zimmet)}
                                className="bg-green-600 hover:bg-green-700 text-white h-8"
                                data-testid={`demirbas-teslim-al-${zimmet.id}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Teslim Al
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleShowGecmis(zimmet)}
                              className="text-gray-600 hover:text-gray-800 h-8 w-8 p-0"
                              title="Geçmişi Gör"
                            >
                              <History className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenTutanak(zimmet)}
                              className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 h-8 gap-1"
                              title="Teslim/Tutanak Oluştur"
                              data-testid={`tutanak-olustur-${zimmet.id}`}
                            >
                              <FileText className="h-4 w-4" />
                              Tutanak
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAŞERONLAR SEKMESİ */}
        <TabsContent value="taseronlar">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-emerald-600" />
                  Taşeronlara Verilen Malzemeler
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {/* Bilgilendirme Kutusu */}
              {showTaseronInfo && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg relative">
                  <button 
                    onClick={() => {
                      setShowTaseronInfo(false);
                      localStorage.setItem('depot_taseron_info_hidden', 'true');
                    }}
                    className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-800">
                      <strong>Taşeron Malzemeleri:</strong> Bu bölümde şubenizde çalışan taşeronlara verdiğiniz malzemeleri takip edebilirsiniz. 
                      Bir taşerona tıklayarak verilen malzemeleri görebilir ve yeni malzeme kaydı oluşturabilirsiniz.
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Sol Panel - Taşeron Listesi */}
                <div className="lg:col-span-1">
                  <div className="border rounded-lg">
                    <div className="p-3 bg-gray-50 border-b">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Taşeron ara..."
                          value={taseronSearchTerm}
                          onChange={(e) => setTaseronSearchTerm(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    <div className="max-h-[500px] overflow-y-auto">
                      {taseronLoading ? (
                        <div className="p-4 text-center text-gray-500">Yükleniyor...</div>
                      ) : taseronlar.filter(t => 
                        t.name?.toLowerCase().includes(taseronSearchTerm.toLowerCase())
                      ).length === 0 ? (
                        <div className="p-4 text-center text-gray-500">
                          {taseronSearchTerm ? 'Taşeron bulunamadı' : 'Henüz taşeron bulunmuyor'}
                        </div>
                      ) : (
                        taseronlar
                          .filter(t => t.name?.toLowerCase().includes(taseronSearchTerm.toLowerCase()))
                          .map((taseron) => (
                            <div
                              key={taseron.id}
                              onClick={() => handleTaseronSelect(taseron)}
                              className={`p-3 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                                selectedTaseron?.id === taseron.id ? 'bg-emerald-50 border-l-4 border-l-emerald-500' : ''
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Building2 className={`h-4 w-4 ${selectedTaseron?.id === taseron.id ? 'text-emerald-600' : 'text-gray-400'}`} />
                                  <span className="font-medium">{taseron.name}</span>
                                </div>
                                {taseron.malzeme_sayisi > 0 && (
                                  <Badge variant="outline" className="text-xs">
                                    {taseron.malzeme_sayisi} malzeme
                                  </Badge>
                                )}
                              </div>
                              {taseron.contact_person && (
                                <div className="text-xs text-gray-500 mt-1 ml-6">
                                  {taseron.contact_person}
                                </div>
                              )}
                            </div>
                          ))
                      )}
                    </div>
                  </div>
                </div>

                {/* Sağ Panel - Malzeme Listesi */}
                <div className="lg:col-span-2">
                  {!selectedTaseron ? (
                    <div className="border rounded-lg p-8 text-center text-gray-500 bg-gray-50">
                      <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>Malzemeleri görüntülemek için sol taraftan bir taşeron seçin</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg">
                      <div className="p-3 bg-emerald-50 border-b flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-5 w-5 text-emerald-600" />
                          <span className="font-semibold text-emerald-800">{selectedTaseron.name}</span>
                          <Badge className="bg-emerald-100 text-emerald-700">
                            {taseronMalzemeleri.length} kayıt
                          </Badge>
                        </div>
                      </div>
                      
                      {taseronMalzemeLoading ? (
                        <div className="p-8 text-center text-gray-500">Yükleniyor...</div>
                      ) : taseronMalzemeleri.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                          <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                          <p>Bu taşerona henüz malzeme verilmemiş</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowTaseronMalzemeModal(true)}
                            className="mt-3 gap-1"
                          >
                            <Plus className="h-4 w-4" />
                            İlk Malzemeyi Ver
                          </Button>
                        </div>
                      ) : (
                        <div className="max-h-[450px] overflow-y-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-gray-50">
                                <TableHead>Tarih</TableHead>
                                <TableHead>Ürün Adı</TableHead>
                                <TableHead className="text-center">Miktar</TableHead>
                                <TableHead>Teslim Alan</TableHead>
                                <TableHead>Teslim Eden</TableHead>
                                <TableHead>Belgeler</TableHead>
                                <TableHead className="w-12"></TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {taseronMalzemeleri.map((malzeme) => (
                                <TableRow key={malzeme.id} className="hover:bg-gray-50">
                                  <TableCell className="text-sm">
                                    <div>{new Date(malzeme.teslim_tarihi || malzeme.created_at).toLocaleDateString('tr-TR')}</div>
                                    <div className="text-xs text-gray-400">
                                      {new Date(malzeme.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                  </TableCell>
                                  <TableCell className="font-medium">{malzeme.urun_adi}</TableCell>
                                  <TableCell className="text-center">
                                    <Badge variant="outline">
                                      {malzeme.miktar} {malzeme.birim}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>{malzeme.teslim_alan || '-'}</TableCell>
                                  <TableCell className="text-sm text-gray-600">{malzeme.teslim_eden || malzeme.created_by_name}</TableCell>
                                  <TableCell>
                                    {malzeme.belgeler && malzeme.belgeler.length > 0 ? (
                                      <div className="flex gap-1">
                                        {malzeme.belgeler.map((belge, idx) => (
                                          <div key={idx} className="flex items-center gap-1">
                                            <a href={belge.data} target="_blank" rel="noopener noreferrer" title="Görüntüle">
                                              <Eye className="h-4 w-4 text-blue-600 hover:text-blue-800" />
                                            </a>
                                            <button onClick={() => {
                                              const link = document.createElement('a');
                                              link.href = belge.data;
                                              link.download = belge.name;
                                              link.click();
                                            }} title="İndir">
                                              <Download className="h-4 w-4 text-blue-600 hover:text-blue-800" />
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteTaseronMalzeme(malzeme.id)}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Teslim Alanlar Sekmesi */}
        <TabsContent value="teslim-alanlar">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-purple-600" />
                Teslim Alanlar ({teslimAlanlar.length} kişi)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Bilgilendirme Kutusu */}
              {showTeslimAlanlarInfo && (
                <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg relative">
                  <button
                    onClick={() => {
                      setShowTeslimAlanlarInfo(false);
                      localStorage.setItem('depot_teslim_alanlar_info_hidden', 'true');
                    }}
                    className="absolute top-2 right-2 p-1 hover:bg-purple-100 rounded"
                  >
                    <X className="h-4 w-4 text-purple-500" />
                  </button>
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-purple-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-purple-800">Teslim Alanlar Hakkında</h4>
                      <p className="text-sm text-purple-600 mt-1">
                        Bu bölümde stok çıkışlarında ürün teslim alan kişilerin listesi ve aldıkları malzemeler görüntülenir.
                        Bir kişiye tıklayarak detaylı teslim geçmişini görebilirsiniz.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Arama */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Teslim alan kişi ara..."
                    value={teslimAlanSearchTerm}
                    onChange={(e) => setTeslimAlanSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {teslimAlanlarLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* Sol Panel - Teslim Alanlar Listesi */}
                  <div className="lg:col-span-1 border rounded-lg p-4 max-h-[600px] overflow-y-auto">
                    <h3 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Kişiler
                    </h3>
                    {teslimAlanlar
                      .filter(k => k.ad?.toLowerCase().includes(teslimAlanSearchTerm.toLowerCase()))
                      .map((kisi) => (
                        <div
                          key={kisi.ad}
                          onClick={() => handleTeslimAlanSelect(kisi)}
                          className={cn(
                            "p-3 rounded-lg cursor-pointer mb-2 transition-colors border",
                            selectedTeslimAlan?.ad === kisi.ad
                              ? "bg-purple-100 border-purple-300"
                              : "hover:bg-gray-50 border-transparent"
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center">
                                <User className="h-4 w-4 text-purple-700" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{kisi.ad || 'İsimsiz'}</p>
                                {kisi.firmalar && kisi.firmalar.length > 0 && (
                                  <p className="text-xs text-emerald-700 flex items-center gap-1">
                                    <Building2 className="h-3 w-3" />
                                    {kisi.firmalar.join(', ')}
                                  </p>
                                )}
                                <p className="text-xs text-gray-500">
                                  Son: {kisi.son_cikis_tarihi ? new Date(kisi.son_cikis_tarihi).toLocaleDateString('tr-TR') : '-'}
                                </p>
                              </div>
                            </div>
                            <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                              {kisi.toplam_cikis} ürün
                            </Badge>
                          </div>
                        </div>
                      ))}
                    {teslimAlanlar.filter(k => k.ad?.toLowerCase().includes(teslimAlanSearchTerm.toLowerCase())).length === 0 && (
                      <p className="text-gray-500 text-center py-4">Kişi bulunamadı</p>
                    )}
                  </div>

                  {/* Sağ Panel - Teslim Detayları */}
                  <div className="lg:col-span-2 border rounded-lg p-4">
                    {selectedTeslimAlan ? (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-purple-200 flex items-center justify-center">
                              <User className="h-6 w-6 text-purple-700" />
                            </div>
                            <div>
                              <h3 className="font-bold text-lg text-gray-900">{selectedTeslimAlan.ad}</h3>
                              <p className="text-sm text-gray-500">
                                Toplam {selectedTeslimAlan.toplam_cikis} ürün teslim almış
                              </p>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-purple-700 border-purple-300">
                            {selectedTeslimAlan.cikislar?.length || 0} çıkış kaydı
                          </Badge>
                        </div>

                        {/* Teslim Geçmişi Tablosu */}
                        <div className="max-h-[450px] overflow-y-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Tarih</TableHead>
                                <TableHead>Ürün</TableHead>
                                <TableHead>Miktar</TableHead>
                                <TableHead>Durum</TableHead>
                                <TableHead>Talep Eden</TableHead>
                                <TableHead>Teslim Alan</TableHead>
                                <TableHead>Teslim Eden</TableHead>
                                <TableHead>Taşeron</TableHead>
                                <TableHead>Açıklama</TableHead>
                                <TableHead>Teslim Tutanağı</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {selectedTeslimAlan.cikislar?.flatMap((cikis, cikisIndex) => 
                                cikis.items?.map((item, itemIndex) => (
                                  <TableRow key={`${cikisIndex}-${itemIndex}`}>
                                    <TableCell className="text-sm">
                                      {cikis.tarih ? new Date(cikis.tarih).toLocaleString('tr-TR', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      }) : '-'}
                                    </TableCell>
                                    <TableCell className="font-medium">{item.product_name || item.urun_adi}</TableCell>
                                    <TableCell>
                                      <Badge variant="outline">
                                        {item.quantity || item.miktar} {item.unit || item.birim || 'adet'}
                                      </Badge>
                                    </TableCell>
                                    <TableCell>
                                      {cikis.approval_status === 'approved' ? (
                                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                                          <Check className="h-3 w-3 mr-1" />
                                          Onaylandı
                                        </Badge>
                                      ) : cikis.approval_status === 'rejected' ? (
                                        <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                                          <X className="h-3 w-3 mr-1" />
                                          Red Etti
                                        </Badge>
                                      ) : (
                                        <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">
                                          <Clock className="h-3 w-3 mr-1" />
                                          Beklemede
                                        </Badge>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-sm text-gray-600">
                                      {cikis.talep_eden || '-'}
                                    </TableCell>
                                    <TableCell className="text-sm text-gray-600">
                                      {cikis.teslim_alan || '-'}
                                    </TableCell>
                                    <TableCell className="text-sm text-gray-600">
                                      {cikis.teslim_eden || '-'}
                                    </TableCell>
                                    <TableCell className="text-sm text-gray-600">
                                      {cikis.taseron_firma || '-'}
                                    </TableCell>
                                    <TableCell className="text-sm text-gray-500 max-w-[150px] truncate">
                                      {cikis.aciklama || '-'}
                                    </TableCell>
                                    <TableCell>
                                      {cikis.teslim_tutanaklari && cikis.teslim_tutanaklari.length > 0 ? (
                                        <div className="space-y-1">
                                          {cikis.teslim_tutanaklari.map((belge, bIndex) => {
                                            let docUrl = belge.url || belge;
                                            if (docUrl && !docUrl.startsWith('http')) {
                                              docUrl = `${API}${docUrl}`;
                                            }
                                            return (
                                              <div key={bIndex} className="flex items-center gap-1 bg-green-50 border border-green-200 rounded px-2 py-1">
                                                <FileText className="h-3 w-3 text-green-600" />
                                                <span className="text-xs text-green-700 truncate max-w-[60px]" title={belge.name}>
                                                  {belge.name || 'Tutanak'}
                                                </span>
                                                {docUrl && (
                                                  <a 
                                                    href={docUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-500 hover:text-blue-700 ml-auto"
                                                    title="Görüntüle/İndir"
                                                  >
                                                    <Download className="h-3 w-3" />
                                                  </a>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      ) : (
                                        <span className="text-gray-400 text-xs">-</span>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-[400px] text-gray-500">
                        <User className="h-16 w-16 mb-4 text-gray-300" />
                        <p className="text-lg font-medium">Kişi Seçin</p>
                        <p className="text-sm">Soldaki listeden bir kişi seçerek teslim geçmişini görüntüleyin</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Zimmet Ekleme Modalı */}
      <Dialog open={showZimmetModal} onOpenChange={setShowZimmetModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-blue-600" />
              Yeni Zimmet Ürünü Ekle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ürün Adı *</Label>
                <Input
                  value={zimmetForm.urun_adi}
                  onChange={(e) => setZimmetForm(prev => ({ ...prev, urun_adi: e.target.value }))}
                  placeholder="Ürün adı..."
                />
              </div>
              <div className="space-y-2">
                <Label>Marka</Label>
                <Input
                  value={zimmetForm.marka}
                  onChange={(e) => setZimmetForm(prev => ({ ...prev, marka: e.target.value }))}
                  placeholder="Marka..."
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Model</Label>
                <Input
                  value={zimmetForm.model}
                  onChange={(e) => setZimmetForm(prev => ({ ...prev, model: e.target.value }))}
                  placeholder="Model..."
                />
              </div>
              <div className="space-y-2">
                <Label>Seri No</Label>
                <Input
                  value={zimmetForm.seri_no}
                  onChange={(e) => setZimmetForm(prev => ({ ...prev, seri_no: e.target.value }))}
                  placeholder="Seri numarası..."
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Textarea
                value={zimmetForm.aciklama}
                onChange={(e) => setZimmetForm(prev => ({ ...prev, aciklama: e.target.value }))}
                placeholder="Açıklama (opsiyonel)..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowZimmetModal(false)}>İptal</Button>
            <Button onClick={handleAddZimmet} className="bg-blue-600 hover:bg-blue-700">Ürün Ekle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zimmet Verme Modalı */}
      <Dialog open={showZimmetVerModal} onOpenChange={setShowZimmetVerModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-blue-600" />
              Zimmet Ver
            </DialogTitle>
          </DialogHeader>
          {selectedZimmet && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium">{selectedZimmet.urun_adi}</p>
                <p className="text-sm text-gray-500">
                  {selectedZimmet.marka} {selectedZimmet.model} - {selectedZimmet.seri_no}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Zimmetlenecek Kişi *</Label>
                <Input
                  list="zimmet-ver-list"
                  value={zimmetVerForm.kullanici_adi}
                  onChange={(e) => {
                    const val = e.target.value;
                    const matched = subeCalisanlari.find(c => c.name === val);
                    setZimmetVerForm(prev => ({ ...prev, kullanici_adi: val, kullanici_id: matched ? matched.id : '' }));
                  }}
                  placeholder="Kişiyi seçin veya yazın..."
                  data-testid="zimmet-ver-kullanici-input"
                />
                <datalist id="zimmet-ver-list">
                  {subeCalisanlari.map((c) => (
                    <option key={c.id} value={c.name} />
                  ))}
                </datalist>
                {zimmetVerForm.kullanici_adi && !zimmetVerForm.kullanici_id && (
                  <p className="text-xs text-amber-600">Bu isim sistemdeki bir kullanıcıyla eşleşmiyor; zimmet kişinin sayfasına düşmeyebilir.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Açıklama</Label>
                <Textarea
                  value={zimmetVerForm.aciklama}
                  onChange={(e) => setZimmetVerForm(prev => ({ ...prev, aciklama: e.target.value }))}
                  placeholder="Not (opsiyonel)..."
                  rows={2}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowZimmetVerModal(false); setSelectedZimmet(null); }}>İptal</Button>
            <Button onClick={handleZimmetVer} className="bg-blue-600 hover:bg-blue-700">Zimmetle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zimmet Geçmişi Modalı */}
      {/* TUTANAK OLUŞTUR MODAL */}
      <Dialog open={tutanakModalOpen} onOpenChange={setTutanakModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="tutanak-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-600" />
              Tutanak Oluştur
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Tutanak Türü */}
            <div>
              <Label>Tutanak Türü</Label>
              <Select
                value={tutanakForm.tutanak_turu}
                onValueChange={(v) => setTutanakForm((f) => ({ ...f, tutanak_turu: v }))}
              >
                <SelectTrigger data-testid="tutanak-turu-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="teslim_etme">Teslim Etme Tutanağı</SelectItem>
                  <SelectItem value="teslim_alma">Teslim Alma Tutanağı</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Teslim Eden */}
              <div>
                <Label>Teslim Eden <span className="text-red-500">*</span></Label>
                <Popover open={edenPickerOpen} onOpenChange={setEdenPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="tutanak-eden-trigger">
                      <span className="truncate">{tutanakForm.teslim_eden || 'Kişi seçin veya yazın'}</span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput
                        placeholder="Ara veya yazıp Enter'a basın..."
                        value={tutanakForm.teslim_eden}
                        onValueChange={(v) => setTutanakForm((f) => ({ ...f, teslim_eden: v }))}
                      />
                      <CommandList>
                        <CommandEmpty>Liste boş - elle yazabilirsiniz</CommandEmpty>
                        <CommandGroup>
                          {tutanakKisiler.map((k) => (
                            <CommandItem
                              key={`eden-${k.id}`}
                              value={`${k.name} ${k.firma}`}
                              onSelect={() => { setTutanakForm((f) => ({ ...f, teslim_eden: k.name })); setEdenPickerOpen(false); }}
                            >
                              <Check className={cn('mr-2 h-4 w-4', tutanakForm.teslim_eden === k.name ? 'opacity-100' : 'opacity-0')} />
                              <span>{k.name}</span>
                              <span className="ml-auto text-xs text-gray-400">{k.firma}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Teslim Alan */}
              <div>
                <Label>Teslim Alan <span className="text-red-500">*</span></Label>
                <Popover open={alanPickerOpen} onOpenChange={setAlanPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="tutanak-alan-trigger">
                      <span className="truncate">{tutanakForm.teslim_alan || 'Kişi seçin veya yazın'}</span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput
                        placeholder="Ara veya yazıp Enter'a basın..."
                        value={tutanakForm.teslim_alan}
                        onValueChange={(v) => setTutanakForm((f) => ({ ...f, teslim_alan: v }))}
                      />
                      <CommandList>
                        <CommandEmpty>Liste boş - elle yazabilirsiniz</CommandEmpty>
                        <CommandGroup>
                          {tutanakKisiler.map((k) => (
                            <CommandItem
                              key={`alan-${k.id}`}
                              value={`${k.name} ${k.firma}`}
                              onSelect={() => { setTutanakForm((f) => ({ ...f, teslim_alan: k.name })); setAlanPickerOpen(false); }}
                            >
                              <Check className={cn('mr-2 h-4 w-4', tutanakForm.teslim_alan === k.name ? 'opacity-100' : 'opacity-0')} />
                              <span>{k.name}</span>
                              <span className="ml-auto text-xs text-gray-400">{k.firma}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Ürün Bilgileri */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Ürün Adı</Label>
                <Input value={tutanakForm.urun_adi} onChange={(e) => setTutanakForm((f) => ({ ...f, urun_adi: e.target.value }))} data-testid="tutanak-urun-adi" />
              </div>
              <div>
                <Label>Marka</Label>
                <Input value={tutanakForm.marka} onChange={(e) => setTutanakForm((f) => ({ ...f, marka: e.target.value }))} />
              </div>
              <div>
                <Label>Model</Label>
                <Input value={tutanakForm.model} onChange={(e) => setTutanakForm((f) => ({ ...f, model: e.target.value }))} />
              </div>
              <div>
                <Label>Seri No</Label>
                <Input value={tutanakForm.seri_no} onChange={(e) => setTutanakForm((f) => ({ ...f, seri_no: e.target.value }))} />
              </div>
            </div>

            {/* İade Tarihi */}
            <div>
              <Label>İade Tarihi (opsiyonel)</Label>
              <Input type="date" value={tutanakForm.iade_tarihi} onChange={(e) => setTutanakForm((f) => ({ ...f, iade_tarihi: e.target.value }))} data-testid="tutanak-iade-tarihi" />
            </div>

            {/* Açıklama */}
            <div>
              <Label>Açıklama</Label>
              <Textarea value={tutanakForm.aciklama} onChange={(e) => setTutanakForm((f) => ({ ...f, aciklama: e.target.value }))} rows={3} placeholder="Tutanak ile ilgili notlar..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTutanakModalOpen(false)} disabled={tutanakSubmitting}>İptal</Button>
            <Button
              onClick={handleDownloadTutanak}
              disabled={tutanakSubmitting}
              className="bg-emerald-600 hover:bg-emerald-700 gap-1"
              data-testid="tutanak-download-btn"
            >
              {tutanakSubmitting ? (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              PDF Oluştur ve İndir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={showGecmisModal} onOpenChange={setShowGecmisModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-gray-600" />
              Zimmet Geçmişi
            </DialogTitle>
          </DialogHeader>
          {selectedZimmet && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium">{selectedZimmet.urun_adi}</p>
                <p className="text-sm text-gray-500">
                  {selectedZimmet.marka} {selectedZimmet.model} - {selectedZimmet.seri_no}
                </p>
              </div>
              {zimmetGecmisi.length === 0 ? (
                <p className="text-center text-gray-500 py-6">Henüz işlem geçmişi bulunmuyor</p>
              ) : (
                <div className="max-h-[420px] overflow-y-auto space-y-3">
                  {zimmetGecmisi.map((gecmis) => {
                    if (gecmis.islem === 'demirbas_islem' && gecmis._event) {
                      const ev = gecmis._event;
                      const colorMap = {
                        emerald: { text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500' },
                        indigo: { text: 'text-indigo-700', bg: 'bg-indigo-50', border: 'border-indigo-200', dot: 'bg-indigo-500' },
                        orange: { text: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200', dot: 'bg-orange-500' },
                        red: { text: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500' },
                        blue: { text: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-500' },
                        slate: { text: 'text-slate-700', bg: 'bg-slate-50', border: 'border-slate-200', dot: 'bg-slate-500' },
                        gray: { text: 'text-gray-700', bg: 'bg-gray-50', border: 'border-gray-200', dot: 'bg-gray-400' },
                      };
                      const c = colorMap[ev.color] || colorMap.gray;
                      const iconMap = {
                        received: CheckCircle, assign: UserCheck, return: User,
                        status: AlertTriangle, transfer: Building2, create: Package, note: History,
                      };
                      const EvIcon = iconMap[ev.iconKey] || History;
                      return (
                        <div key={gecmis.id} className={`border rounded-lg overflow-hidden ${c.border}`}>
                          <div className={`flex items-center justify-between px-3 py-2 ${c.bg}`}>
                            <div className="flex items-center gap-2">
                              <EvIcon className={`h-4 w-4 ${c.text}`} />
                              <span className={`font-semibold text-sm ${c.text}`}>{ev.title}</span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {new Date(gecmis.islem_tarihi).toLocaleString('tr-TR')}
                            </span>
                          </div>
                          <div className="p-3 space-y-2">
                            {ev.rows.length > 0 && (
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                {ev.rows.map((r, ri) => (
                                  <div key={ri} className="text-sm">
                                    <span className="text-gray-500">{r.label}: </span>
                                    <span className="font-medium text-gray-800">{r.value || '-'}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {ev.note && <p className="text-sm text-gray-600 italic">Not: {ev.note}</p>}
                            {gecmis.belgeler && gecmis.belgeler.length > 0 ? (
                              <div className="pt-1">
                                <span className="text-xs text-gray-500 block mb-1">Belgeler:</span>
                                <div className="flex flex-wrap gap-2">
                                  {gecmis.belgeler.map((belge, bIdx) => (
                                    <a
                                      key={bIdx}
                                      href={belge.url && (belge.url.startsWith('http') || belge.url.startsWith('data:')) ? belge.url : `${API}${belge.url}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      download={belge.dosya_adi}
                                      className="text-xs text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded flex items-center gap-1"
                                    >
                                      <FileText className="h-3.5 w-3.5" />
                                      <Download className="h-3 w-3" />
                                      {belge.dosya_adi || 'Belge'}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <p className="text-xs text-gray-400">Belge eklenmemiş</p>
                            )}
                          </div>
                        </div>
                      );
                    }
                    return (
                    <div key={gecmis.id} className="border rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {gecmis.islem === 'zimmet_ver' && (
                            <>
                              <UserCheck className="h-4 w-4 text-blue-600" />
                              <span className="text-blue-700 font-medium">Zimmet Verildi</span>
                              <span className="text-gray-600">→ {gecmis.kullanici_adi}</span>
                            </>
                          )}
                          {gecmis.islem === 'zimmette_ver' && (
                            <>
                              <UserCheck className="h-4 w-4 text-indigo-600" />
                              <span className="text-indigo-700 font-medium">Kalıcı Zimmet Verildi</span>
                              <span className="text-gray-600">→ {gecmis.kullanici_adi}</span>
                            </>
                          )}
                          {gecmis.islem === 'zimmet_al' && (
                            <>
                              <User className="h-4 w-4 text-orange-600" />
                              <span className="text-orange-700 font-medium">Zimmet Alındı</span>
                              <span className="text-gray-600">← {gecmis.kullanici_adi}</span>
                            </>
                          )}
                          {gecmis.islem === 'durum_degisikligi' && (
                            <>
                              <AlertTriangle className="h-4 w-4 text-yellow-600" />
                              <span className="text-yellow-700 font-medium">Durum Değişikliği</span>
                              <span className="text-gray-600">{gecmis.eski_durum} → {gecmis.yeni_durum}</span>
                            </>
                          )}
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(gecmis.islem_tarihi).toLocaleString('tr-TR')}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">İşlemi yapan: {gecmis.islem_yapan_adi}</p>
                      {gecmis.aciklama && <p className="text-sm text-gray-600 mt-1">{gecmis.aciklama}</p>}
                      {gecmis.belge_url && (!gecmis.belgeler || gecmis.belgeler.length === 0) && (
                        <div className="mt-2 flex items-center gap-2 bg-blue-50 p-2 rounded">
                          <FileText className="h-4 w-4 text-blue-600" />
                          <a 
                            href={`${API}${gecmis.belge_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                          >
                            <Download className="h-3 w-3" />
                            Belgeyi İndir
                          </a>
                        </div>
                      )}
                      {gecmis.belgeler && gecmis.belgeler.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {gecmis.belgeler.map((belge, bIdx) => (
                            <a
                              key={bIdx}
                              href={belge.url && (belge.url.startsWith('http') || belge.url.startsWith('data:')) ? belge.url : `${API}${belge.url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded flex items-center gap-1"
                            >
                              <FileText className="h-3.5 w-3.5" />
                              <Download className="h-3 w-3" />
                              {belge.dosya_adi || 'Belge'}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowGecmisModal(false); setSelectedZimmet(null); }}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Durum Değişikliği Modalı */}
      <Dialog open={showDurumModal} onOpenChange={(open) => {
        if (!open) {
          setShowDurumModal(false);
          setDurumChangeData({ zimmet: null, yeniDurum: '', aciklama: '', tamir_yeri: '', tahmini_sure: '', bozukluk_nedeni: '', kayip_tarihi: '', kayip_yeri: '' });
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {durumChangeData.yeniDurum === 'depoda' && <Package className="h-5 w-5 text-gray-600" />}
              {durumChangeData.yeniDurum === 'kullanicida' && <UserCheck className="h-5 w-5 text-blue-600" />}
              {durumChangeData.yeniDurum === 'zimmette' && <UserCheck className="h-5 w-5 text-indigo-600" />}
              {durumChangeData.yeniDurum === 'bozuk' && <AlertTriangle className="h-5 w-5 text-red-600" />}
              {durumChangeData.yeniDurum === 'tamirde' && <Wrench className="h-5 w-5 text-yellow-600" />}
              {durumChangeData.yeniDurum === 'kayip' && <HelpCircle className="h-5 w-5 text-purple-600" />}
              {durumChangeData.yeniDurum === 'demirbas' && <ClipboardList className="h-5 w-5 text-emerald-600" />}
              {getDurumModalTitle(durumChangeData.yeniDurum)}
            </DialogTitle>
          </DialogHeader>
          
          {durumChangeData.zimmet && (
            <div className="space-y-4">
              {/* Ürün Bilgisi */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium">{durumChangeData.zimmet.urun_adi}</p>
                <p className="text-sm text-gray-500">
                  {durumChangeData.zimmet.marka} {durumChangeData.zimmet.model} - {durumChangeData.zimmet.seri_no}
                </p>
              </div>

              {/* Kullanıcıda / Zimmette durumu için özel alan */}
              {(durumChangeData.yeniDurum === 'kullanicida' || durumChangeData.yeniDurum === 'zimmette') && (
                <div className="space-y-2">
                  <Label>Kullanıcı Seçin *</Label>
                  <Select
                    value={durumChangeData.kullanici_adi || ''}
                    onValueChange={(val) => setDurumChangeData(prev => ({ ...prev, kullanici_adi: val }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Kullanıcı seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {subeCalisanlari.length === 0 ? (
                        <SelectItem value="_none" disabled>Çalışan bulunamadı</SelectItem>
                      ) : (
                        subeCalisanlari.map((calisan) => (
                          <SelectItem key={calisan.id} value={calisan.name}>
                            {calisan.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {durumChangeData.yeniDurum === 'zimmette' && (
                    <p className="text-xs text-indigo-600 bg-indigo-50 border border-indigo-200 rounded p-2">
                      Kalıcı zimmet: Ürün, kişi işten çıkana veya farklı bir durum oluşana kadar bu kişide kalacak şekilde işaretlenir (kısa süreli al-bırak için "Kullanıcıda" kullanın).
                    </p>
                  )}
                </div>
              )}

              {/* Tamirde durumu için özel alanlar */}
              {durumChangeData.yeniDurum === 'tamirde' && (
                <>
                  <div className="space-y-2">
                    <Label>Tamir Yeri *</Label>
                    <Input
                      placeholder="Servis adı veya tamir yeri"
                      value={durumChangeData.tamir_yeri}
                      onChange={(e) => setDurumChangeData(prev => ({ ...prev, tamir_yeri: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tahmini Süre</Label>
                    <Input
                      placeholder="Örn: 1 hafta, 3 gün"
                      value={durumChangeData.tahmini_sure}
                      onChange={(e) => setDurumChangeData(prev => ({ ...prev, tahmini_sure: e.target.value }))}
                    />
                  </div>
                </>
              )}

              {/* Bozuk durumu için özel alan */}
              {durumChangeData.yeniDurum === 'bozuk' && (
                <div className="space-y-2">
                  <Label>Bozukluk Nedeni *</Label>
                  <Input
                    placeholder="Arıza açıklaması"
                    value={durumChangeData.bozukluk_nedeni}
                    onChange={(e) => setDurumChangeData(prev => ({ ...prev, bozukluk_nedeni: e.target.value }))}
                  />
                </div>
              )}

              {/* Kayıp durumu için özel alanlar */}
              {durumChangeData.yeniDurum === 'kayip' && (
                <>
                  <div className="space-y-2">
                    <Label>Kayıp Tarihi *</Label>
                    <Input
                      type="date"
                      value={durumChangeData.kayip_tarihi}
                      onChange={(e) => setDurumChangeData(prev => ({ ...prev, kayip_tarihi: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Kayıp Yeri</Label>
                    <Input
                      placeholder="Son görüldüğü yer"
                      value={durumChangeData.kayip_yeri}
                      onChange={(e) => setDurumChangeData(prev => ({ ...prev, kayip_yeri: e.target.value }))}
                    />
                  </div>
                </>
              )}

              {/* Genel açıklama alanı - tüm durumlar için */}
              <div className="space-y-2">
                <Label>Açıklama {durumChangeData.yeniDurum === 'depoda' ? '*' : ''}</Label>
                <Textarea
                  placeholder="Ek açıklama veya notlar..."
                  value={durumChangeData.aciklama}
                  onChange={(e) => setDurumChangeData(prev => ({ ...prev, aciklama: e.target.value }))}
                  rows={3}
                />
              </div>

              {/* Belge Ekleme Alanı - Demirbaşlar için opsiyonel */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Paperclip className="h-4 w-4" />
                  Belge Ekle {durumChangeData.zimmet?.is_demirbas ? '(Opsiyonel)' : '*'}
                </Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors">
                  <input
                    type="file"
                    id="durum-belge-input"
                    className="hidden"
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length > 0) {
                        setDurumChangeData(prev => ({ ...prev, belge: [...(prev.belge || []), ...files] }));
                      }
                      e.target.value = '';
                    }}
                  />
                  {durumChangeData.belge && durumChangeData.belge.length > 0 ? (
                    <div className="space-y-2">
                      {durumChangeData.belge.map((f, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-gray-50 rounded p-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="h-5 w-5 text-blue-600 shrink-0" />
                            <span className="text-sm font-medium truncate">{f.name}</span>
                            <span className="text-xs text-gray-500 shrink-0">
                              ({(f.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setDurumChangeData(prev => ({ ...prev, belge: prev.belge.filter((_, i) => i !== idx) }))}
                            className="text-red-500 hover:text-red-700 shrink-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <label
                        htmlFor="durum-belge-input"
                        className="flex items-center justify-center gap-2 cursor-pointer py-2 text-sm text-blue-600 hover:text-blue-800 border border-dashed border-blue-300 rounded"
                      >
                        <Upload className="h-4 w-4" />
                        Başka belge ekle
                      </label>
                    </div>
                  ) : (
                    <label
                      htmlFor="durum-belge-input"
                      className="flex flex-col items-center justify-center cursor-pointer py-2"
                    >
                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600">Belge seçmek için tıklayın (birden fazla seçilebilir)</span>
                      <span className="text-xs text-gray-400 mt-1">PDF, DOC, DOCX, JPG, PNG</span>
                    </label>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => {
              setShowDurumModal(false);
              setDurumChangeData({ zimmet: null, yeniDurum: '', aciklama: '', kullanici_adi: '', tamir_yeri: '', tahmini_sure: '', bozukluk_nedeni: '', kayip_tarihi: '', kayip_yeri: '', belge: [] });
            }} disabled={durumBelgeUploading}>
              İptal
            </Button>
            <Button 
              onClick={confirmDurumChange}
              className={
                durumChangeData.yeniDurum === 'bozuk' ? 'bg-red-600 hover:bg-red-700' :
                durumChangeData.yeniDurum === 'tamirde' ? 'bg-yellow-600 hover:bg-yellow-700' :
                durumChangeData.yeniDurum === 'kayip' ? 'bg-purple-600 hover:bg-purple-700' :
                durumChangeData.yeniDurum === 'kullanicida' ? 'bg-blue-600 hover:bg-blue-700' :
                durumChangeData.yeniDurum === 'zimmette' ? 'bg-indigo-600 hover:bg-indigo-700' :
                'bg-gray-600 hover:bg-gray-700'
              }
              disabled={
                durumBelgeUploading ||
                (!durumChangeData.zimmet?.is_demirbas && (!durumChangeData.belge || durumChangeData.belge.length === 0)) ||
                (durumChangeData.yeniDurum === 'tamirde' && !durumChangeData.tamir_yeri) ||
                (durumChangeData.yeniDurum === 'bozuk' && !durumChangeData.bozukluk_nedeni) ||
                (durumChangeData.yeniDurum === 'kayip' && !durumChangeData.kayip_tarihi) ||
                ((durumChangeData.yeniDurum === 'kullanicida' || durumChangeData.yeniDurum === 'zimmette') && !durumChangeData.kullanici_adi) ||
                (durumChangeData.yeniDurum === 'depoda' && !durumChangeData.aciklama)
              }
            >
              {durumBelgeUploading ? 'Yükleniyor...' : 'Onayla'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Taşeron Malzeme Ekleme Modalı */}
      <Dialog open={showTaseronMalzemeModal} onOpenChange={setShowTaseronMalzemeModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-emerald-600" />
              Taşerona Malzeme Ver
            </DialogTitle>
          </DialogHeader>
          {selectedTaseron && (
            <div className="p-2 bg-emerald-50 rounded-lg mb-4">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-emerald-600" />
                <span className="font-medium text-emerald-800">{selectedTaseron.name}</span>
              </div>
            </div>
          )}
          <div className="space-y-4">
            {/* Stok Kartı Seçimi */}
            <div className="space-y-2">
              <Label>Stok Kartı (Opsiyonel)</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between">
                    {taseronMalzemeForm.stok_karti_id 
                      ? stokKartlari.find(s => s.id === taseronMalzemeForm.stok_karti_id)?.name || 'Seçildi'
                      : 'Stok kartı seç veya manuel gir...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput placeholder="Stok kartı ara..." />
                    <CommandList>
                      <CommandEmpty>Stok kartı bulunamadı</CommandEmpty>
                      <CommandGroup>
                        {stokKartlari.map((stok) => (
                          <CommandItem
                            key={stok.id}
                            onSelect={() => {
                              setTaseronMalzemeForm(prev => ({
                                ...prev,
                                stok_karti_id: stok.id,
                                urun_adi: stok.name,
                                birim: stok.unit || 'adet'
                              }));
                            }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", taseronMalzemeForm.stok_karti_id === stok.id ? "opacity-100" : "opacity-0")} />
                            <div className="flex flex-col">
                              <span>{stok.name}</span>
                              <span className="text-xs text-gray-500">Stok: {stok.mevcut_stok || 0} {stok.unit}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Ürün Adı */}
            <div className="space-y-2">
              <Label>Ürün Adı *</Label>
              <Input
                value={taseronMalzemeForm.urun_adi}
                onChange={(e) => setTaseronMalzemeForm(prev => ({ ...prev, urun_adi: e.target.value }))}
                placeholder="Ürün adı..."
              />
            </div>

            {/* Miktar ve Birim */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Miktar *</Label>
                <Input
                  type="number"
                  value={taseronMalzemeForm.miktar}
                  onChange={(e) => setTaseronMalzemeForm(prev => ({ ...prev, miktar: e.target.value }))}
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="space-y-2">
                <Label>Birim</Label>
                <select
                  value={taseronMalzemeForm.birim}
                  onChange={(e) => setTaseronMalzemeForm(prev => ({ ...prev, birim: e.target.value }))}
                  className="w-full h-10 px-3 border border-gray-300 rounded-md"
                >
                  <option value="adet">Adet</option>
                  <option value="kg">Kg</option>
                  <option value="metre">Metre</option>
                  <option value="litre">Litre</option>
                  <option value="paket">Paket</option>
                  <option value="kutu">Kutu</option>
                  <option value="ton">Ton</option>
                  <option value="m2">m²</option>
                </select>
              </div>
            </div>

            {/* Teslim Alan */}
            <div className="space-y-2">
              <Label>Teslim Alan Kişi</Label>
              <Input
                value={taseronMalzemeForm.teslim_alan}
                onChange={(e) => setTaseronMalzemeForm(prev => ({ ...prev, teslim_alan: e.target.value }))}
                placeholder="Malzemeyi teslim alan kişi..."
              />
            </div>

            {/* Açıklama */}
            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Textarea
                value={taseronMalzemeForm.aciklama}
                onChange={(e) => setTaseronMalzemeForm(prev => ({ ...prev, aciklama: e.target.value }))}
                placeholder="Ek notlar..."
                rows={2}
              />
            </div>

            {/* Belge Yükleme */}
            <div className="space-y-2">
              <Label>Belge Ekle</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        setTaseronMalzemeForm(prev => ({
                          ...prev,
                          belgeler: [...prev.belgeler, { name: file.name, data: ev.target.result }]
                        }));
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="flex-1"
                />
              </div>
              {taseronMalzemeForm.belgeler.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {taseronMalzemeForm.belgeler.map((belge, idx) => (
                    <Badge key={idx} variant="outline" className="gap-1">
                      <FileText className="h-3 w-3" />
                      {belge.name}
                      <button
                        onClick={() => setTaseronMalzemeForm(prev => ({
                          ...prev,
                          belgeler: prev.belgeler.filter((_, i) => i !== idx)
                        }))}
                        className="ml-1 text-red-500 hover:text-red-700"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowTaseronMalzemeModal(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleTaseronMalzemeSubmit}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? 'Kaydediliyor...' : 'Malzeme Ver'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DepotExit;
